import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import './Dashboard.css';
import PurchaseGraph from '../components/PurchaseGraph';
import GenrePicker from '../components/GenrePicker';
import ArticlePreview from '../components/ArticlePreview';
import SettingsPanel from '../components/SettingsPanel';
import ArticleEditor, { buildPreviewHTML, imgsMissingAlt } from '../components/ArticleEditor';
import orangeLogo from '../assets/orangelog.png';
import { uploadImage } from '../lib/uploadImage';
import DraftPreview from '../components/DraftPreview';

const T = {
  accent:  '#dd3333',
  header:  '#111111',
  border:  '#e8e8e8',
  bodyText:'#444',
  headText:'#111',
  meta:    '#777',
  lightBg: '#f4f4f4',
  white:   '#fff',
};

function CompanyDashboard() {
  const navigate = useNavigate();
  const { accessToken: token, clearSession } = useAuth();
  const [company, setCompany] = useState(null);
  const [articles, setArticles] = useState([]);
  const [periods, setPeriods] = useState({ allTime: {}, monthly: {}, daily: {} });
  const [statPeriod, setStatPeriod] = useState('monthly');
  // legacy — kept so nothing downstream breaks
  const [allTimeRevenue, setAllTimeRevenue] = useState(0);
  const [allTimeGross, setAllTimeGross] = useState(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [monthlyGross, setMonthlyGross] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showExtForm, setShowExtForm] = useState(false);
  const [extForm, setExtForm] = useState({
    external_url: '',
    title: '',
    writer_name: '',
    publish_date: '',
    token_cost: 4,
    content: '',
    headline_image: null,
    cardStyle: '6',
  });
  const [formData, setFormData] = useState({
    title: '',
    writer_name: '',
    content: '',
    audience: 'local',
    token_cost: 4,
    headline_image: null,
  });
  const [createPricingMode, setCreatePricingMode] = useState('manual');
  const [createAutoAdjust, setCreateAutoAdjust] = useState({ firstDecreaseDays: '', decreasePercent: '', frequencyDays: '', skipIfDailyAbove: '' });
  const [genreTree, setGenreTree] = useState({ main: '', sub: '' });
  const [extGenreTree, setExtGenreTree] = useState({ main: '', sub: '' });
  const [submittedArticle, setSubmittedArticle] = useState(null);

  // Sort + filter state
  const [sortBy, setSortBy] = useState('date_desc');
  const [filterGenre, setFilterGenre] = useState('');
  const [filterAuthor, setFilterAuthor] = useState('');
  const [filterPricingMode, setFilterPricingMode] = useState('');
  const [filterDateRange, setFilterDateRange] = useState('');

  // Dropdown state
  const [expandedId, setExpandedId] = useState(null);
  const [pricingForm, setPricingForm] = useState({ mode: 'manual', firstDecreaseDays: '', decreasePercent: '', frequencyDays: '', skipIfDailyAbove: '' });
  const [pricingMsg, setPricingMsg] = useState('');
  const [pricingLoading, setPricingLoading] = useState(false);
  const [priceChangeForm, setPriceChangeForm] = useState(''); // new token cost input
  const [priceChangeMsg, setPriceChangeMsg] = useState('');
  const [priceChangeLoading, setPriceChangeLoading] = useState(false);

  // Article deletion
  const [deleteTarget, setDeleteTarget] = useState(null); // { _id, title, purchased, lastPurchasedAt }
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Sunset date
  const [sunsetEdit, setSunsetEdit] = useState({}); // { [articleId]: dateString }
  const [sunsetMsg, setSunsetMsg]   = useState({}); // { [articleId]: messageString }

  // Local-date helpers — avoids UTC midnight shifting dates back one day
  const localDateYMD = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
  };
  const localDateDisplay = (isoStr) => {
    const d = new Date(isoStr);
    // Sunset dates are stored as UTC midnight (date-only selections) — shift to local noon
    // to prevent timezone drift flipping the date back one day.
    // Real timestamps (lastPurchasedAt) have a non-zero time component — let the browser
    // convert them to local time directly.
    if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) {
      return new Date(isoStr.slice(0, 10) + 'T12:00:00').toLocaleDateString();
    }
    return d.toLocaleDateString();
  };
  const tomorrowYMD = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return localDateYMD(d); })();

  // Live analytics
  const [liveVisitors, setLiveVisitors] = useState(0);
  const [liveReaders, setLiveReaders] = useState({}); // { articleId: count }
  const liveIntervalRef = useRef(null);

  // Graph modal
  const [graphArticle, setGraphArticle] = useState(null); // { _id, title }
  // Preview modal
  const [previewArticle, setPreviewArticle] = useState(null);
  const [showDraftPreview, setShowDraftPreview] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Editors management
  const [editors, setEditors]               = useState([]);
  const [showEditorForm, setShowEditorForm] = useState(false);
  const [editorForm, setEditorForm]         = useState({ display_name: '', username: '', email: '', password: '' });
  const [editorMsg, setEditorMsg]           = useState('');
  const [removeEditorTarget, setRemoveEditorTarget]   = useState(null);
  const [removeEditorPassword, setRemoveEditorPassword] = useState('');
  const [removeEditorError, setRemoveEditorError]       = useState('');
  const [removeEditorLoading, setRemoveEditorLoading]   = useState(false);
  // Author assignment modal for editors
  const [assignTarget, setAssignTarget]     = useState(null); // editor being edited
  const [assignSelected, setAssignSelected] = useState([]); // array of authorId strings
  const [assignMsg, setAssignMsg]           = useState('');
  // Editor gear dropdown + edit modal
  const [editorGearOpen, setEditorGearOpen]     = useState(null); // editor _id with open dropdown
  const [editEditorTarget, setEditEditorTarget] = useState(null);
  const [editEditorForm, setEditEditorForm]     = useState({ display_name: '', department: '' });
  const [editEditorMsg, setEditEditorMsg]       = useState('');

  // Authors management
  const [authors, setAuthors]               = useState([]);
  const [showAuthorForm, setShowAuthorForm] = useState(false);
  const [authorForm, setAuthorForm]         = useState({ display_name: '', username: '', email: '', password: '', department: '' });
  const [authorMsg, setAuthorMsg]           = useState('');
  // Remove-author confirmation modal
  const [removeTarget, setRemoveTarget]     = useState(null); // { _id, display_name }
  const [removePassword, setRemovePassword] = useState('');
  const [removeError, setRemoveError]       = useState('');
  const [removeLoading, setRemoveLoading]   = useState(false);
  // Author gear dropdown + edit modal
  const [authorGearOpen, setAuthorGearOpen] = useState(null); // author _id with open dropdown
  const [editAuthorTarget, setEditAuthorTarget] = useState(null); // author being edited
  const [editAuthorForm, setEditAuthorForm] = useState({ display_name: '', department: '' });
  const [editAuthorMsg, setEditAuthorMsg]   = useState('');

  // Settings gear (Quick Actions area)
  const [settingsGearOpen, setSettingsGearOpen] = useState(false);
  const [settingsView, setSettingsView]         = useState(null); // null | 'embed' | 'logo' | 'w9' | 'billing'
  // Billing email state
  const [billingEmailVal, setBillingEmailVal]   = useState('');
  const [billingEmailMsg, setBillingEmailMsg]   = useState('');
  const [billingEmailLoading, setBillingEmailLoading] = useState(false);
  // W-9 form state
  const [w9Form, setW9Form] = useState({ legalName: '', businessName: '', taxClassification: '', llcMemberType: '', address: '', cityStateZip: '', tinType: '', tin: '', certified: false });
  const [w9Msg, setW9Msg]   = useState('');
  const [w9Loading, setW9Loading] = useState(false);
  // Company support contact modal
  const [supportOpen, setSupportOpen]   = useState(false);
  const [supportTopic, setSupportTopic] = useState('');
  const [supportMsg, setSupportMsg]     = useState('');
  const [supportSending, setSupportSending] = useState(false);
  const [supportResult, setSupportResult]   = useState(null); // null | 'ok' | 'err'
  const [supportErr, setSupportErr]         = useState('');
  // Logo upload state
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoMsg, setLogoMsg]             = useState('');

  // Embed Integration state
  const [embedOpen, setEmbedOpen]             = useState(false); // collapsed by default
  const [embedInstructionsOpen, setEmbedInstructionsOpen] = useState(false);
  const [embedKeyOnce, setEmbedKeyOnce]       = useState(null); // shown once on generation
  const [embedSecretOnce, setEmbedSecretOnce] = useState(null); // shown once on generation
  const [embedKeysLoading, setEmbedKeysLoading] = useState(false);
  const [embedKeysMsg, setEmbedKeysMsg]       = useState('');
  const [embedArticles, setEmbedArticles]     = useState([]); // Grand tier: auto-created articles
  const [embedPriceEdit, setEmbedPriceEdit]   = useState({}); // { [id]: newPrice }
  const [embedModeEdit, setEmbedModeEdit]     = useState({}); // { [id]: 'manual'|'auto' }
  const [embedAutoEdit, setEmbedAutoEdit]     = useState({}); // { [id]: { firstDecreaseDate, decreasePercent, frequencyDays, skipIfDailyAbove } }
  const [embedPriceMsg, setEmbedPriceMsg]     = useState('');
  // Elevated tier button generator
  const [elGenArticle, setElGenArticle]       = useState('');
  const [elGenReturn, setElGenReturn]         = useState('');
  const [elGenSnippet, setElGenSnippet]       = useState('');
  // Grand tier button generator
  const [grGenSlug, setGrGenSlug]             = useState('');
  const [grGenTitle, setGrGenTitle]           = useState('');
  const [grGenPrice, setGrGenPrice]           = useState('');
  const [grGenReturn, setGrGenReturn]         = useState('');
  const [grGenAudience, setGrGenAudience]     = useState('local');
  const [grGenSnippet, setGrGenSnippet]       = useState('');
  const [grGenMsg, setGrGenMsg]               = useState(''); // registration confirmation

  const DEPT_OPTIONS = [
    'Politics', 'Business & Economy', 'Technology', 'World/International',
    'Health', 'Science & Environment', 'Sports', 'Entertainment & Culture',
    'Crime & Justice', 'Opinion & Analysis', 'Other',
  ];

  useEffect(() => {
    fetchDashboardData();
    fetchAuthors();
    fetchEditors();
    fetchEmbedArticles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAuthors = async () => {
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      const res = await axios.get('/api/companies/me/authors', { headers: authHeader });
      setAuthors(res.data);
    } catch (err) { /* silent */ }
  };

  const handleAddAuthor = async (e) => {
    e.preventDefault();
    setAuthorMsg('');
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.post('/api/companies/me/authors', authorForm, { headers: authHeader });
      setAuthorForm({ display_name: '', username: '', email: '', password: '', department: '' });
      setShowAuthorForm(false);
      setAuthorMsg('Author added successfully.');
      fetchAuthors();
    } catch (err) {
      setAuthorMsg(err.response?.data?.error || 'Failed to add author.');
    }
  };

  const handleEditAuthorSubmit = async (e) => {
    e.preventDefault();
    setEditAuthorMsg('');
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/companies/me/authors/${editAuthorTarget._id}`, editAuthorForm, { headers: authHeader });
      setEditAuthorTarget(null);
      setAuthorMsg('Author updated successfully.');
      fetchAuthors();
    } catch (err) {
      setEditAuthorMsg(err.response?.data?.error || 'Failed to update author.');
    }
  };

  const handleRemoveAuthorConfirm = async () => {
    if (!removeTarget || !removePassword) return;
    setRemoveError('');
    setRemoveLoading(true);
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.delete(`/api/companies/me/authors/${removeTarget._id}`, {
        headers: authHeader,
        data: { password: removePassword },
      });
      setRemoveTarget(null);
      setRemovePassword('');
      setAuthorMsg(`${removeTarget.display_name} has been removed.`);
      fetchAuthors();
    } catch (err) {
      setRemoveError(err.response?.data?.error || 'Failed to remove author.');
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleToggleTrusted = async (authorId, currentTrusted) => {
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/companies/me/authors/${authorId}`, { trusted: !currentTrusted }, { headers: authHeader });
      fetchAuthors();
    } catch (err) {
      setAuthorMsg(err.response?.data?.error || 'Failed to update author.');
    }
  };

  const fetchEditors = async () => {
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      const res = await axios.get('/api/companies/me/editors', { headers: authHeader });
      setEditors(res.data);
    } catch (err) { /* silent */ }
  };

  const handleAddEditor = async (e) => {
    e.preventDefault();
    setEditorMsg('');
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.post('/api/companies/me/editors', editorForm, { headers: authHeader });
      setEditorForm({ display_name: '', username: '', email: '', password: '' });
      setShowEditorForm(false);
      setEditorMsg('Editor added successfully.');
      fetchEditors();
    } catch (err) {
      setEditorMsg(err.response?.data?.error || 'Failed to add editor.');
    }
  };

  const handleEditEditorSubmit = async (e) => {
    e.preventDefault();
    setEditEditorMsg('');
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/companies/me/editors/${editEditorTarget._id}`, editEditorForm, { headers: authHeader });
      setEditEditorTarget(null);
      setEditorMsg('Editor updated successfully.');
      fetchEditors();
    } catch (err) {
      setEditEditorMsg(err.response?.data?.error || 'Failed to update editor.');
    }
  };

  const handleRemoveEditorConfirm = async () => {
    if (!removeEditorTarget || !removeEditorPassword) return;
    setRemoveEditorError('');
    setRemoveEditorLoading(true);
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.delete(`/api/companies/me/editors/${removeEditorTarget._id}`, {
        headers: authHeader,
        data: { password: removeEditorPassword },
      });
      setRemoveEditorTarget(null);
      setRemoveEditorPassword('');
      setEditorMsg(`${removeEditorTarget.display_name} has been removed.`);
      fetchEditors();
    } catch (err) {
      setRemoveEditorError(err.response?.data?.error || 'Failed to remove editor.');
    } finally {
      setRemoveEditorLoading(false);
    }
  };

  const handleAssignAuthors = async () => {
    if (!assignTarget) return;
    setAssignMsg('');
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/companies/me/editors/${assignTarget._id}/authors`, { authorIds: assignSelected }, { headers: authHeader });
      setAssignMsg('Authors updated.');
      fetchEditors();
    } catch (err) {
      setAssignMsg(err.response?.data?.error || 'Failed to update authors.');
    }
  };

  // Start polling live analytics once we have the company ID
  useEffect(() => {
    if (!company) return;
    const authHeader = { Authorization: `Bearer ${token}` };

    const poll = () => {
      axios.get(`/api/analytics/live/company/${company._id}`, { headers: authHeader })
        .then(res => {
          setLiveVisitors(res.data.visitors || 0);
          setLiveReaders(res.data.articleReaders || {});
        })
        .catch(() => {});
    };

    poll();
    liveIntervalRef.current = setInterval(poll, 30000);
    return () => clearInterval(liveIntervalRef.current);
  }, [company]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const authHeader = { Authorization: `Bearer ${token}` };
      const response = await axios.get('/api/companies/me/dashboard', { headers: authHeader });
      setCompany(response.data.company);
      setBillingEmailVal(response.data.company?.billingEmail || '');
      setExtForm(f => ({ ...f, cardStyle: response.data.company?.cardStyle || '6' }));
      setArticles(response.data.articles);
      setPeriods(response.data.periods || { allTime: {}, monthly: {}, daily: {} });
      setAllTimeRevenue(response.data.allTimeRevenue);
      setAllTimeGross(response.data.allTimeGross);
      setMonthlyRevenue(response.data.monthlyRevenue);
      setMonthlyGross(response.data.monthlyGross ?? response.data.monthlyRevenue);
    } catch (err) {
      if (err.response?.status === 403) {
        clearSession();
        navigate('/login');
        return;
      }
      console.error('Error fetching data:', err);
      setError('Failed to load dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (token) axios.post('/api/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    clearSession();
    navigate('/');
  };

  const handleCreateArticle = () => { setShowForm(true); };

  const handleSubmitExternal = async (e) => {
    e.preventDefault();
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      const articleData = {
        article_id: `ext-${Date.now()}`,
        title:        extForm.title,
        writer_name:  extForm.writer_name,
        content:      extForm.content,
        token_cost:   Math.min(99, Math.max(0, parseFloat(extForm.token_cost) || 0)),
        external_url: extForm.external_url,
        headline_image: extForm.headline_image || null,
        publish_date: extForm.publish_date || undefined,
        genre_tree:   extGenreTree,
        audience:     'national',
        locations:    [],
        pricingMode:  'manual',
        cardStyle:    extForm.cardStyle,
      };
      const response = await axios.post('/api/articles', articleData, { headers: authHeader });
      setSubmittedArticle(response.data);
      setShowExtForm(false);
      setExtForm({ external_url: '', title: '', writer_name: '', publish_date: '', token_cost: 0, content: '', headline_image: null, cardStyle: company?.cardStyle || '6' });
      setExtGenreTree({ main: '', sub: '' });
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create external article');
    }
  };

  // Embed: fetch Grand-tier articles
  const fetchEmbedArticles = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get('/api/embed/articles', { headers });
      setEmbedArticles(res.data);
    } catch { /* not grand tier — silently ignore */ }
  };

  // Embed: generate keys
  const handleGenerateKeys = async (force = false) => {
    setEmbedKeysLoading(true);
    setEmbedKeysMsg('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post('/api/embed/keys', force ? { force: true } : {}, { headers });
      setEmbedKeyOnce(res.data.embedKey);
      setEmbedSecretOnce(res.data.embedSecret);
      await fetchDashboardData(); // refresh company (new embedKey visible)
      await fetchEmbedArticles();
      setEmbedKeysMsg('Keys generated. Store the secret securely — it will not be shown again.');
    } catch (err) {
      setEmbedKeysMsg(err.response?.data?.error || 'Failed to generate keys.');
    } finally {
      setEmbedKeysLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setLogoMsg('Image must be under 5 MB.'); return; }
    setLogoUploading(true);
    setLogoMsg('');
    try {
      const url = await uploadImage(file, token);
      await axios.patch('/api/companies/me/logo', { hero_image: url }, { headers: { Authorization: `Bearer ${token}` } });
      setCompany(prev => ({ ...prev, hero_image: url }));
      setLogoMsg('Logo updated.');
      setTimeout(() => setLogoMsg(''), 3000);
    } catch (err) {
      setLogoMsg(err.response?.data?.error || err.message || 'Upload failed.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleW9Submit = async (e) => {
    e.preventDefault();
    setW9Msg('');
    if (!w9Form.certified) { setW9Msg('You must certify the information is correct before submitting.'); return; }
    setW9Loading(true);
    try {
      await axios.patch('/api/companies/me/w9', {
        legalName:         w9Form.legalName,
        businessName:      w9Form.businessName,
        taxClassification: w9Form.taxClassification,
        llcMemberType:     w9Form.llcMemberType || undefined,
        address:           w9Form.address,
        cityStateZip:      w9Form.cityStateZip,
        tinType:           w9Form.tinType,
        tin:               w9Form.tin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCompany(prev => ({ ...prev, w9: { submitted: true, submittedAt: new Date().toISOString() } }));
      setW9Msg('success');
    } catch (err) {
      setW9Msg(err.response?.data?.error || 'Submission failed. Please try again.');
    } finally {
      setW9Loading(false);
    }
  };

  // Embed: update price / pricingMode / autoAdjust on a Grand-tier article
  const handleEmbedPriceUpdate = async (artId) => {
    const art = embedArticles.find(a => a._id === artId);
    if (!art) return;
    const payload = {};
    if (embedPriceEdit[artId] !== undefined) payload.price = Number(embedPriceEdit[artId]);
    const mode = embedModeEdit[artId] !== undefined ? embedModeEdit[artId] : art.pricingMode;
    if (embedModeEdit[artId] !== undefined) payload.pricingMode = mode;
    if (mode === 'auto' && embedAutoEdit[artId]) payload.autoAdjust = embedAutoEdit[artId];
    if (Object.keys(payload).length === 0) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.patch(`/api/embed/article/${artId}/price`, payload, { headers });
      setEmbedArticles(prev => prev.map(a => a._id === artId ? { ...a, ...res.data } : a));
      setEmbedPriceEdit(prev => { const n = { ...prev }; delete n[artId]; return n; });
      setEmbedModeEdit(prev => { const n = { ...prev }; delete n[artId]; return n; });
      setEmbedAutoEdit(prev => { const n = { ...prev }; delete n[artId]; return n; });
      setEmbedPriceMsg('Saved.');
      setTimeout(() => setEmbedPriceMsg(''), 3000);
    } catch (err) {
      setEmbedPriceMsg(err.response?.data?.error || 'Failed to save.');
    }
  };

  // Elevated: generate button snippet
  const handleElGenerate = () => {
    if (!elGenArticle || !elGenReturn) return;
    const base = window.location.origin;
    const href = `${base}/verify?article=${encodeURIComponent(elGenArticle)}&return_to=${encodeURIComponent(elGenReturn)}`;
    const snippet =
`<!-- PubSilo purchase button -->
<a href="${href}">
  <img src="${base}/pubsilo-button.png" alt="Purchase on PubSilo" title="Purchase on PubSilo" style="height:40px;cursor:pointer;">
</a>

<!-- Paste once per page, before </body> -->
<script>
(function(){
  var p=new URLSearchParams(location.search),art=p.get('article'),tok=p.get('token');
  if(!art||!tok)return;
  history.replaceState({},'',location.pathname);
  fetch('${base}/api/embed/verify?article='+art+'&token='+tok)
    .then(function(r){return r.json()})
    .then(function(d){if(d.valid)unlockContent(art)});
})();
</script>`;
    setElGenSnippet(snippet);
  };

  // Grand: pre-register article (locks price server-side), then generate snippet
  const handleGrGenerate = async () => {
    if (!grGenSlug || !grGenReturn || !company?.embedKey) return;
    setGrGenMsg('Registering…');
    setGrGenSnippet('');
    const base = window.location.origin;
    const params = new URLSearchParams({ key: company.embedKey, article: grGenSlug, title: grGenTitle, price: grGenPrice, return_to: grGenReturn, audience: grGenAudience });
    const href = `${base}/buy?${params}`;

    try {
      // Pre-register creates the article record now, so the ?price= URL param is
      // ignored by all readers — including the very first one.
      const regParams = new URLSearchParams({ key: company.embedKey, article: grGenSlug, title: grGenTitle, price: grGenPrice, return_to: grGenReturn, audience: grGenAudience });
      const res = await axios.get(`/api/embed/article?${regParams}`);
      const lockedPrice = res.data.token_cost;
      setGrGenMsg(`✓ Article registered — price locked at ${lockedPrice} Article Credit${lockedPrice !== 1 ? 's' : ''}`);
      fetchEmbedArticles();
    } catch (err) {
      setGrGenMsg(`⚠ Registration failed: ${err.response?.data?.error || 'server error'}. The snippet was still generated but the price is not yet locked.`);
    }

    const snippet =
`<!-- PubSilo purchase button -->
<a href="${href}">
  <img src="${base}/pubsilo-button.png" alt="Purchase on PubSilo" title="Purchase on PubSilo" style="height:40px;cursor:pointer;">
</a>

<!-- Paste once per page, before </body> -->
<script>
(function(){
  var p=new URLSearchParams(location.search),art=p.get('article'),tok=p.get('token');
  if(!art||!tok)return;
  history.replaceState({},'',location.pathname);
  fetch('${base}/api/embed/verify?key=${company.embedKey}&article='+art+'&token='+tok)
    .then(function(r){return r.json()})
    .then(function(d){if(d.valid)unlockContent(art)});
})();
</script>`;
    setGrGenSnippet(snippet);
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmitArticle = async (e, saveAsDraft = false) => {
    e.preventDefault();
    setError('');
    if (!genreTree.main) {
      setError('Please select a genre category.');
      return;
    }
    const rawCost = parseInt(formData.token_cost, 10);
    if (isNaN(rawCost) || rawCost < 4 || rawCost > 150) {
      setError('Article credit price must be between 4 and 150.');
      return;
    }
    if (!saveAsDraft) {
      const missing = imgsMissingAlt(formData.content);
      if (missing > 0) {
        setError(`${missing} image${missing > 1 ? 's are' : ' is'} missing alt text. Click each image in the editor and add an accessibility description before publishing.`);
        return;
      }
    }
    const articleData = {
      article_id: `${company.link_id}-${Date.now()}`,
      writer_name: formData.writer_name
        ? formData.writer_name.split(',').map(n => n.trim()).filter(n => n)
        : [company.company_name],
      title: formData.title,
      content: formData.content,
      genre_tree: genreTree,
      audience: formData.audience,
      token_cost: rawCost,
      headline_image: formData.headline_image || null,
      pricingMode: createPricingMode,
      autoAdjust: createPricingMode === 'auto' ? {
        firstDecreaseDate: new Date(Date.now() + Number(createAutoAdjust.firstDecreaseDays) * 86400000),
        decreasePercent:   Number(createAutoAdjust.decreasePercent),
        frequencyDays:     Number(createAutoAdjust.frequencyDays),
        skipIfDailyAbove:  createAutoAdjust.skipIfDailyAbove !== '' ? Number(createAutoAdjust.skipIfDailyAbove) : null,
      } : null,
      ...(saveAsDraft ? { status: 'draft' } : {}),
    };
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      const response = await axios.post('/api/articles', articleData, { headers: authHeader });
      setSubmittedArticle(response.data);
      setShowForm(false);
      setFormData({ title: '', writer_name: '', content: '', audience: 'local', token_cost: 4, headline_image: null });
      setCreatePricingMode('manual');
      setCreateAutoAdjust({ firstDecreaseDays: '', decreasePercent: '', frequencyDays: '', skipIfDailyAbove: '' });
      setGenreTree({ main: '', sub: '' });
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create article');
    }
  };

  const toggleDropdown = (articleId) => {
    if (expandedId === articleId) {
      setExpandedId(null);
      setPricingMsg('');
      setPriceChangeForm('');
      setPriceChangeMsg('');
    } else {
      setExpandedId(articleId);
      setPricingMsg('');
      const art = articles.find(a => a._id === articleId);
      if (art) {
        const adj = art.autoAdjust || {};
        const daysFromNow = adj.firstDecreaseDate
          ? Math.max(1, Math.round((new Date(adj.firstDecreaseDate) - Date.now()) / 86400000))
          : '';
        setPricingForm({
          mode: art.pricingMode || 'manual',
          firstDecreaseDays: adj.firstDecreaseDate  ? String(daysFromNow)              : '',
          decreasePercent:   adj.decreasePercent  != null ? String(adj.decreasePercent) : '',
          frequencyDays:     adj.frequencyDays    != null ? String(adj.frequencyDays)   : '',
          skipIfDailyAbove:  adj.skipIfDailyAbove != null ? String(adj.skipIfDailyAbove): '',
        });
      }
    }
  };

  const handleSunsetSave = async (articleId, dateStr) => {
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/articles/${articleId}/sunset`, { sunsetDate: dateStr || null }, { headers: authHeader });
      setSunsetMsg(m => ({ ...m, [articleId]: dateStr ? `Sunset set: ${localDateDisplay(dateStr)}` : 'Sunset cleared.' }));
      setSunsetEdit(s => { const next = { ...s }; delete next[articleId]; return next; });
      fetchDashboardData();
      setTimeout(() => setSunsetMsg(m => ({ ...m, [articleId]: '' })), 4000);
    } catch (err) {
      setSunsetMsg(m => ({ ...m, [articleId]: err.response?.data?.error || 'Failed to save.' }));
    }
  };

  const handlePublishDraft = async (articleId) => {
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/articles/${articleId}/status`, { status: 'approved' }, { headers: authHeader });
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to publish draft.');
    }
  };

  const handleReturnToDraft = async (articleId) => {
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/articles/${articleId}/status`, { status: 'draft' }, { headers: authHeader });
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to return article to draft.');
    }
  };

  const handleDeleteArticle = async () => {
    if (!deleteTarget || !deletePassword) return;
    setDeleteError('');
    setDeleteLoading(true);
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.delete(`/api/articles/${deleteTarget._id}`, {
        headers: authHeader,
        data: { password: deletePassword },
      });
      setDeleteTarget(null);
      setDeletePassword('');
      fetchDashboardData();
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'Failed to delete article.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePricingSubmit = async (article) => {
    setPricingMsg('');
    setPricingLoading(true);
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      const body = { pricingMode: pricingForm.mode };
      if (pricingForm.mode === 'auto') {
        body.autoAdjust = {
          firstDecreaseDate: new Date(Date.now() + Number(pricingForm.firstDecreaseDays) * 86400000),
          decreasePercent:   Number(pricingForm.decreasePercent),
          frequencyDays:     Number(pricingForm.frequencyDays),
          skipIfDailyAbove:  pricingForm.skipIfDailyAbove !== '' ? Number(pricingForm.skipIfDailyAbove) : null,
        };
      }
      await axios.patch(`/api/articles/${article._id}/pricing`, body, { headers: authHeader });
      setPricingMsg(pricingForm.mode === 'manual' ? 'Pricing set to manual.' : 'Auto-adjust schedule saved.');
      fetchDashboardData();
    } catch (err) {
      setPricingMsg(err.response?.data?.error || 'Failed to save pricing settings.');
    } finally {
      setPricingLoading(false);
    }
  };

  const handlePriceChange = async (article) => {
    const newCost = Number(priceChangeForm);
    if (isNaN(newCost) || newCost < 0) { setPriceChangeMsg('Enter a valid credit price.'); return; }
    if (newCost === article.token_cost) { setPriceChangeMsg('That is already the current price.'); return; }
    setPriceChangeMsg('');
    setPriceChangeLoading(true);
    try {
      const authHeader = { Authorization: `Bearer ${token}` };
      await axios.patch(`/api/articles/${article._id}/token-cost`, { token_cost: newCost }, { headers: authHeader });
      setPriceChangeForm('');
      setPriceChangeMsg(`Price updated to ${newCost} Article Credits.`);
      fetchDashboardData();
    } catch (err) {
      setPriceChangeMsg(err.response?.data?.error || 'Failed to update price.');
    } finally {
      setPriceChangeLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: T.white }}>
        <nav style={{ background: T.header, display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
          </div>
          <div style={{ gridColumn: 2, gridRow: 1, background: '#13273F' }} />
          <div style={{ gridColumn: 3, gridRow: '1 / 3', background: '#13273F' }} />
          <div style={{ gridColumn: 2, gridRow: 2, background: '#13273F' }} />
        </nav>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 110px)', color: T.meta, fontFamily: 'Arial, sans-serif' }}>
          Loading your dashboard...
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: T.white, fontFamily: "'Arial', sans-serif", color: T.bodyText }}>

      {/* ── Navbar ── */}
      <nav style={{ background: T.header, display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
        {/* Logo — col 1, rows 1-2 */}
        <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238', cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain' }} />
        </div>
        {/* Center top — col 2, row 1 */}
        <div style={{ gridColumn: 2, gridRow: 1, background: company?.navbar_color || '#13273F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ color: '#aaa', fontSize: '13px', fontFamily: 'Arial, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>Publisher Dashboard</div>
          {company?.hero_image ? (
            <img
              src={company.hero_image}
              alt={company.company_name}
              style={{ maxHeight: '90px', maxWidth: '100%', objectFit: 'contain' }}
            />
          ) : (
            <div style={{ color: T.white, fontSize: '28px', fontWeight: '300', fontFamily: 'Arial, sans-serif', letterSpacing: '0.04em', lineHeight: 1 }}>
              {company?.company_name}
            </div>
          )}
        </div>
        {/* Right: company info + logout — col 3, rows 1-2 */}
        <div style={{ gridColumn: 3, gridRow: '1 / 3', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', background: company?.navbar_color || '#13273F' }}>
          <div style={{ color: T.white, fontSize: '15px', fontFamily: 'Arial, sans-serif', fontWeight: '700' }}>{company?.company_name}</div>
          <div style={{ color: '#aaa', fontSize: '11px', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Publisher</div>
          <button
            onClick={handleLogout}
            style={{ marginTop: '4px', padding: '6px 16px', background: 'transparent', border: '1px solid #3a3a3a', borderRadius: '5px', color: '#ccc', fontSize: '12px', fontFamily: 'Arial, sans-serif', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#222'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ccc'; }}
          >
            Log Out
          </button>
        </div>
        {/* Row 2: quick nav strip */}
        <div style={{ gridColumn: 2, gridRow: 2, display: 'flex', alignItems: 'center', background: company?.navbar_color || '#13273F', borderBottom: '2px solid #000' }}>
          {[
            { label: 'Admin Silo', href: `/adminHUD/${encodeURIComponent(company?.company_name || '')}` },
            { label: 'Company Page ↗', href: `/${encodeURIComponent(company?.company_name || '')}` },
          ].map(({ label, href }) => (
            <a
              key={label}
              href={href}
              style={{
                padding: '0 20px', height: '100%', display: 'flex', alignItems: 'center',
                background: 'transparent', borderBottom: '2px solid transparent',
                color: '#aaa', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase',
                letterSpacing: '0.08em', fontFamily: 'Arial, sans-serif', textDecoration: 'none',
                transition: 'background 0.12s, color 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#aaa'; }}
            >
              {label}
            </a>
          ))}
          <button
            onClick={() => setShowHelpModal(true)}
            style={{
              marginLeft: 'auto', padding: '0 20px', height: '100%', display: 'flex', alignItems: 'center',
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#aaa', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase',
              letterSpacing: '0.08em', fontFamily: 'Arial, sans-serif',
              transition: 'background 0.12s, color 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#aaa'; }}
          >
            ? Help &amp; Guide
          </button>
        </div>
      </nav>

      {/* ── Admin notice / disabled banner ── */}
      {company?.adminMessage && (
        <div style={{ background: '#ff6b00', color: '#fff', padding: '0.75rem 1.5rem', fontFamily: 'Arial, sans-serif', fontSize: '0.88rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <span>⚠️ Notice from PubSilo:</span>
          <span style={{ fontWeight: '400' }}>{company.adminMessage}</span>
        </div>
      )}
      {company?.disabled && !company?.adminMessage && (
        <div style={{ background: '#cc0000', color: '#fff', padding: '0.75rem 1.5rem', fontFamily: 'Arial, sans-serif', fontSize: '0.88rem', fontWeight: '600' }}>
          ⚠️ Your account has been restricted. Contact support for details.
        </div>
      )}
      {company?.deletionFlagged && (
        <div style={{ background: '#e67e22', color: '#fff', padding: '0.65rem 1.5rem', fontFamily: 'Arial, sans-serif', fontSize: '0.85rem', fontWeight: '500' }}>
          ⚠️ Your account has a deletion debt of <strong>${(company.deletionDebt || 0).toFixed(2)}</strong>. Future licensing payments are on hold until this is cleared. Contact support to resolve.
        </div>
      )}

      {/* ── Body ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {/* Left Sidebar */}
        <div style={{ width: '220px', flexShrink: 0, borderRight: `1px solid ${T.border}`, minHeight: 'calc(100vh - 146px)', padding: '16px 12px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#888', paddingBottom: '8px', borderBottom: `2px solid ${T.accent}`, marginBottom: '12px', fontFamily: 'Arial, sans-serif' }}>Company Stats</div>

          {/* Period selector */}
          <select
            value={statPeriod}
            onChange={e => setStatPeriod(e.target.value)}
            style={{ width: '100%', marginBottom: '1rem', padding: '0.4rem 0.5rem', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.82rem', cursor: 'pointer' }}
          >
            <option value="monthly">Monthly (from 1st)</option>
            <option value="daily">Today</option>
            <option value="allTime">All-Time</option>
          </select>

          {(() => {
            const p = periods;
            const isAllTime = statPeriod === 'allTime';
            const isMonthly = statPeriod === 'monthly';

            // Period bucket shorthand
            const bucket = isAllTime ? p.allTime : isMonthly ? p.monthly : p.daily;

            const revenue = isAllTime ? (p.allTime?.net ?? 0) : (bucket?.revenue ?? 0);
            const gross   = isAllTime ? (p.allTime?.gross ?? 0) : null;

            // Articles published in the selected period
            const now = new Date();
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            const dayStart   = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const articlesPublished = isAllTime
              ? articles.length
              : articles.filter(a => {
                  const d = new Date(a.date_created);
                  return isMonthly ? d >= monthStart : d >= dayStart;
                }).length;

            const revenueLabel = isAllTime ? 'Net Revenue (est.)' : isMonthly ? 'Est. Licensing Payment This Month' : 'Revenue Today';
            const periodLabel  = isAllTime ? 'All-Time' : isMonthly ? 'This Month' : 'Today';

            const monthlyFee = periods?.monthly?.feeBreakdown || {};
            const aboveThreshold  = (monthlyFee.tier2Gross || 0) > 0;
            const aboveThreshold2 = (monthlyFee.tier3Gross || 0) > 0;

            const statItem = (val, label, valStyle = {}) => (
              <div style={{ padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ fontSize: '20px', fontWeight: '800', color: T.headText, lineHeight: 1, fontFamily: 'Georgia, serif', ...valStyle }}>{val}</div>
                <div style={{ fontSize: '10px', color: T.meta, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '3px', fontFamily: 'Arial, sans-serif' }}>{label}</div>
              </div>
            );
            return (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {statItem((company?.followers || []).length, 'Followers', { color: T.accent })}
                {statItem(company?.visits || 0, 'Silo Visits')}
                {statItem(
                  (liveVisitors > 0 ? '🟢 ' : '') + liveVisitors,
                  'Live Silo Visitors',
                  { color: liveVisitors > 0 ? '#28a745' : T.headText }
                )}
                {statItem(articlesPublished, `Articles Published${periodLabel === 'All-Time' ? '' : ' — ' + periodLabel}`)}
                {isAllTime && gross != null && statItem(`$${gross.toFixed(2)}`, 'All-Time Gross Access Value')}
                {statItem(`$${revenue.toFixed(2)}`, revenueLabel, { color: '#1a7a3a' })}
                {isMonthly && monthlyGross > 0 && (
                  <div style={{ padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: '10px', color: T.meta, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px', fontFamily: 'Arial, sans-serif' }}>Fee Breakdown This Month</div>
                    <div style={{ fontSize: '11px', color: '#666', fontFamily: 'Arial, sans-serif', lineHeight: 1.7 }}>
                      <div>Gross access value: <strong>${monthlyGross.toFixed(2)}</strong></div>
                      <div>First $2,000 at 15%: <span style={{ color: '#c0710a' }}>−${(monthlyFee.tier1Fee || 0).toFixed(2)}</span></div>
                      {aboveThreshold && (
                        <div>$2,000–$5,000 at 12.5%: <span style={{ color: '#c0710a' }}>−${(monthlyFee.tier2Fee || 0).toFixed(2)}</span></div>
                      )}
                      {aboveThreshold2 && (
                        <div>Above $5,000 at 9%: <span style={{ color: '#c0710a' }}>−${(monthlyFee.tier3Fee || 0).toFixed(2)}</span></div>
                      )}
                      <div>Total fee: <span style={{ color: '#c0710a' }}>−${(monthlyFee.totalFee || 0).toFixed(2)}</span></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Center */}
        <div style={{ flex: 1, minWidth: 0, padding: '20px 24px', borderRight: `1px solid ${T.border}` }}>
          <div style={{ borderBottom: `3px solid ${T.header}`, paddingBottom: '8px', marginBottom: '16px' }}>
            <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', fontWeight: '900', color: T.headText, margin: 0 }}>{company?.company_name} Dashboard</h2>
            <p style={{ fontFamily: 'Arial, sans-serif', fontSize: '12px', color: T.meta, margin: '4px 0 0', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Your Published Articles</p>
          </div>


          {submittedArticle ? (() => {
            const articleUrl = `${window.location.origin}/hub-buy?article=${submittedArticle._id}&company=${encodeURIComponent(company.company_name)}&title=${encodeURIComponent(submittedArticle.title)}&price=${submittedArticle.token_cost}`;
            const embedCode  = `<a href="${articleUrl}">PubSilo (${submittedArticle.token_cost} Article Credits)</a>`;
            return (
              <div className="article-share-card">
                <h3>Article Created Successfully!</h3>
                <p style={{ marginBottom: '0.25rem' }}>Article URL:</p>
                <div className="share-link" style={{ marginBottom: '1rem' }}>
                  <a href={articleUrl} target="_blank" rel="noopener noreferrer">{articleUrl}</a>
                </div>
                <p style={{ marginBottom: '0.25rem' }}>Embed code — copy and paste this anywhere:</p>
                <div style={{
                  backgroundColor: '#f4f4f4', border: '1px solid #ddd', borderRadius: '6px',
                  padding: '0.75rem 1rem', fontFamily: 'monospace', fontSize: '0.9rem',
                  wordBreak: 'break-all', userSelect: 'all', marginBottom: '1rem',
                }}>
                  {embedCode}
                </div>
                <button onClick={() => setSubmittedArticle(null)}>Create Another</button>
              </div>
            );
          })() : showExtForm ? (
            <div className="article-form-card">
              <h3>Create External Link &amp; Article Card</h3>
              <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
                This creates a card in your article feed that links readers to an article hosted on your own website.
              </p>
              <form onSubmit={handleSubmitExternal}>
                <div className="form-group">
                  <label>External Article URL <span style={{ color: '#dc3545' }}>*</span></label>
                  <input
                    type="url"
                    value={extForm.external_url}
                    onChange={e => setExtForm(f => ({ ...f, external_url: e.target.value }))}
                    placeholder="https://yoursite.com/article"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Article Title <span style={{ color: '#dc3545' }}>*</span></label>
                  <input
                    type="text"
                    value={extForm.title}
                    onChange={e => setExtForm(f => ({ ...f, title: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Author Name(s)</label>
                  <input
                    type="text"
                    value={extForm.writer_name}
                    onChange={e => setExtForm(f => ({ ...f, writer_name: e.target.value }))}
                    placeholder="e.g. John Doe"
                  />
                </div>
                <div className="form-group">
                  <label>Publish Date</label>
                  <input
                    type="date"
                    value={extForm.publish_date}
                    onChange={e => setExtForm(f => ({ ...f, publish_date: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Article Credit Price (4–150) <span style={{ color: '#dc3545' }}>*</span></label>
                  <input
                    type="number"
                    value={extForm.token_cost}
                    onChange={e => setExtForm(f => ({ ...f, token_cost: e.target.value }))}
                    min="4" max="150" step="1" required
                  />
                </div>
                <div className="form-group">
                  <label>Short Blurb <span style={{ fontWeight: 400, color: '#888', fontSize: '0.82rem' }}>(shown as preview text on the card)</span></label>
                  <textarea
                    value={extForm.content}
                    onChange={e => setExtForm(f => ({ ...f, content: e.target.value }))}
                    rows={3}
                    placeholder="A brief summary readers will see before purchasing..."
                    style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1.5px solid #ddd', borderRadius: '6px', fontSize: '0.9rem', resize: 'vertical' }}
                  />
                </div>
                <div className="form-group">
                  <label>Attention Image <span style={{ fontWeight: 400, color: '#888', fontSize: '0.82rem' }}>(shown on the right side of the article card)</span></label>
                  <div style={{ fontSize: '0.78rem', color: '#e67e22', marginBottom: '0.35rem', fontWeight: 600 }}>Best results: 1200 × 675 px (16:9 landscape). Portrait or square images will be cropped on feed cards.</div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      try {
                        const url = await uploadImage(file, token);
                        setExtForm(f => ({ ...f, headline_image: url }));
                      } catch (err) {
                        alert(err.message || 'Image upload failed.');
                      }
                    }}
                  />
                  {extForm.headline_image && (
                    <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <img src={extForm.headline_image} alt="preview" style={{ height: '60px', width: '90px', objectFit: 'cover', borderRadius: '5px', border: '1px solid #ddd' }} />
                      <button type="button" onClick={() => setExtForm(f => ({ ...f, headline_image: null }))} style={{ fontSize: '0.78rem', color: '#dc3545', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <GenrePicker onChange={setExtGenreTree} />
                </div>

                {/* Live card preview — full size, vertical, updates as form is filled */}
                {(() => {
                  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                  const compName = company?.company_name || 'Your Company';
                  const title   = extForm.title   || 'Article Headline';
                  const blurb   = extForm.content  || 'Your article blurb will appear here as you type…';
                  const author  = extForm.writer_name;
                  const cost    = Number(extForm.token_cost);
                  const imgNode = (r) => extForm.headline_image
                    ? <img src={extForm.headline_image} alt="" style={{ width: '100%', height: 'auto', maxHeight: '200px', objectFit: 'contain', display: 'block', borderRadius: r }} />
                    : <div style={{ width: '100%', height: '80px', background: '#d0cdc7', borderRadius: r }} />;
                  const readBtnPreview = <span style={{ padding: '0.38rem 1rem', fontSize: '0.82rem', fontWeight: '700', borderRadius: '6px', backgroundColor: '#1a1a1a', color: '#fff', fontFamily: 'Georgia, serif', display: 'inline-block' }}>Read Full Story ↗</span>;
                  const costPreview = cost > 0 && <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#7b2ff7', backgroundColor: '#f3eeff', padding: '0.2rem 0.6rem', borderRadius: '12px' }}>{cost} 🪙</span>;
                  const sqRow6 = <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>{['+','+','↗'].map((s,i) => <span key={i} style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', justifyContent:'center', width:'40px', height:'40px', border:'1px solid #d4d1cb', borderRadius:'5px', background:'#f0ede7', gap:'1px' }}><span style={{ fontSize:'0.72rem', fontWeight:'700', color:'#555' }}>{s}</span><span style={{ fontSize:'0.44rem', color:'#888' }}>{s==='↗'?'Company':'Follow'}</span><span style={{ fontSize:'0.44rem', color:'#888' }}>{s==='↗'?'Silo':i===0?'Publisher':'Author'}</span></span>)}</div>;
                  const sqRowC = <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>{['+','+','↗'].map((s,i) => <span key={i} style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', justifyContent:'center', width:'40px', height:'40px', border:'1px solid #c8c4bb', borderRadius:'5px', background:'#f0ede7', gap:'1px' }}><span style={{ fontSize:'0.72rem', fontWeight:'700', color:'#555' }}>{s}</span><span style={{ fontSize:'0.44rem', color:'#888' }}>{s==='↗'?'Company':'Follow'}</span><span style={{ fontSize:'0.44rem', color:'#888' }}>{s==='↗'?'Silo':i===0?'Publisher':'Author'}</span></span>)}</div>;

                  return (
                    <div style={{ marginTop: '1.25rem', borderTop: '1.5px solid #e0e0e0', paddingTop: '1.1rem' }}>
                      <div style={{ fontWeight: '600', fontSize: '0.88rem', marginBottom: '0.25rem' }}>Choose Card Layout</div>
                      <div style={{ fontSize: '0.76rem', color: '#888', marginBottom: '1.1rem', lineHeight: 1.5 }}>
                        Previews update as you fill out the form above. Select the layout for this article.
                      </div>

                      {/* Style 6 — Two-Column Newspaper */}
                      <label style={{ display: 'block', cursor: 'pointer', marginBottom: '1.25rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.55rem' }}>
                          <input type="radio" name="extCardStyle" checked={extForm.cardStyle === '6'} onChange={() => setExtForm(f => ({ ...f, cardStyle: '6' }))} />
                          <span style={{ fontSize: '0.82rem', fontWeight: '700' }}>Two-Column Newspaper</span>
                        </div>
                        <div style={{ background: '#fafaf8', border: `2px solid ${extForm.cardStyle === '6' ? '#1a1a1a' : '#d8d5cf'}`, borderRadius: '8px', padding: '1.25rem 1.25rem 0', overflow: 'hidden', opacity: extForm.cardStyle === '6' ? 1 : 0.55, transition: 'opacity 0.15s, border-color 0.15s', pointerEvents: 'none' }}>
                          <div style={{ borderBottom: '2px solid #1a1a1a', marginBottom: '0.75rem', paddingBottom: '0.4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.12em', color: '#333' }}>{compName}</span>
                            <span style={{ fontSize: '0.7rem', color: '#888' }}>{today}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '1.25rem' }}>
                            <div style={{ flex: 2 }}>
                              <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '1.15rem', fontWeight: '700', lineHeight: 1.3, margin: '0 0 0.6rem' }}>{title}</h3>
                              <p style={{ fontFamily: 'Georgia, serif', fontSize: '0.87rem', color: '#444', lineHeight: 1.7, margin: 0 }}>{blurb}</p>
                            </div>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                              {imgNode('4px')}
                              {author && <div style={{ fontSize: '0.72rem', color: '#777', fontStyle: 'italic' }}>By {author}</div>}
                            </div>
                          </div>
                          <div style={{ borderTop: '1px solid #e0ddd6', backgroundColor: '#f0ede7', margin: '1rem -1.25rem 0', padding: '0.65rem 1.25rem', display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                            {readBtnPreview}{costPreview}{sqRow6}
                          </div>
                        </div>
                      </label>

                      {/* Style C — Broadsheet Columns */}
                      <label style={{ display: 'block', cursor: 'pointer', marginBottom: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.55rem' }}>
                          <input type="radio" name="extCardStyle" checked={extForm.cardStyle === 'C'} onChange={() => setExtForm(f => ({ ...f, cardStyle: 'C' }))} />
                          <span style={{ fontSize: '0.82rem', fontWeight: '700' }}>Broadsheet Columns</span>
                        </div>
                        <div style={{ background: '#fafaf8', border: `2px solid ${extForm.cardStyle === 'C' ? '#1a1a1a' : '#c8c4bb'}`, borderRadius: '6px', overflow: 'hidden', opacity: extForm.cardStyle === 'C' ? 1 : 0.55, transition: 'opacity 0.15s, border-color 0.15s', pointerEvents: 'none' }}>
                          <div style={{ borderBottom: '2px solid #1a1a1a', padding: '0.55rem 1.1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ fontFamily: 'Georgia, serif', fontSize: '1rem', fontWeight: '700', letterSpacing: '0.05em', color: '#1a1a1a' }}>{compName}</span>
                            <span style={{ fontSize: '0.65rem', color: '#888' }}>{today}</span>
                          </div>
                          <div style={{ display: 'flex' }}>
                            <div style={{ flex: 1, padding: '0 1.1rem', borderRight: '1px solid #c8c4bb' }}>
                              <div style={{ paddingTop: '0.9rem', paddingBottom: '0.9rem' }}>
                                {imgNode('3px')}
                                <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '0.95rem', fontWeight: '700', lineHeight: 1.3, margin: '0.6rem 0 0' }}>{title}</h3>
                              </div>
                            </div>
                            <div style={{ flex: 1, padding: '0 1.1rem' }}>
                              <div style={{ paddingTop: '0.9rem', paddingBottom: '0.9rem' }}>
                                <p style={{ fontFamily: 'Georgia, serif', fontSize: '0.82rem', color: '#444', lineHeight: 1.7, margin: '0 0 0.75rem' }}>{blurb}</p>
                                {author && <div style={{ fontFamily: 'Georgia, serif', fontSize: '0.72rem', color: '#777', fontStyle: 'italic' }}>By {author}</div>}
                              </div>
                            </div>
                          </div>
                          <div style={{ borderTop: '1px solid #c8c4bb', backgroundColor: '#f0ede7', padding: '0.65rem 1.1rem', display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                            {readBtnPreview}{costPreview}{sqRowC}
                          </div>
                        </div>
                      </label>

                    </div>
                  );
                })()}

                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1.1rem' }}>
                  <button type="submit">Create Article Card</button>
                  <button type="button" onClick={() => setShowExtForm(false)}>Cancel</button>
                </div>
              </form>
            </div>
          ) : showForm ? (
            <div className="article-form-card">
              <h3>Create New Article</h3>
              <form onSubmit={handleSubmitArticle}>
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" name="title" value={formData.title} onChange={handleFormChange} required />
                </div>
                <div className="form-group">
                  <label>Author Name(s) (comma-separated)</label>
                  <input type="text" name="writer_name" value={formData.writer_name} onChange={handleFormChange} placeholder="e.g. John Doe, Jane Smith" />
                </div>
                <div className="form-group">
                  <label>Headline Image <span style={{ fontWeight: 400, color: '#888', fontSize: '0.82rem' }}>(optional — shown on article card)</span></label>
                  <div style={{ fontSize: '0.78rem', color: '#e67e22', marginBottom: '0.35rem', fontWeight: 600 }}>Best results: 1200 × 675 px (16:9 landscape). Portrait or square images will be cropped on feed cards.</div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      try {
                        const url = await uploadImage(file, token);
                        setFormData(f => ({ ...f, headline_image: url }));
                      } catch (err) {
                        alert(err.message || 'Image upload failed.');
                      }
                    }}
                  />
                  {formData.headline_image && (
                    <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <img src={formData.headline_image} alt="preview" style={{ height: '60px', width: '90px', objectFit: 'cover', borderRadius: '5px', border: '1px solid #ddd' }} />
                      <button type="button" onClick={() => setFormData(f => ({ ...f, headline_image: null }))} style={{ fontSize: '0.78rem', color: '#dc3545', background: 'none', border: 'none', cursor: 'pointer' }}>Remove</button>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Content</label>
                  <ArticleEditor
                    initialContent={formData.content}
                    onChange={html => setFormData(f => ({ ...f, content: html }))}
                    placeholder="Write your article..."
                  />
                </div>
                <div className="form-group">
                  <GenrePicker onChange={setGenreTree} />
                </div>
                <div className="form-group">
                  <label>Audience</label>
                  <select name="audience" value={formData.audience} onChange={handleFormChange}>
                    <option value="world">World</option>
                    <option value="national">National</option>
                    <option value="regional">Regional (multi-state)</option>
                    <option value="state">State</option>
                    <option value="local">Local</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Article Credit Price (4–150)</label>
                  <input type="number" name="token_cost" value={formData.token_cost} onChange={handleFormChange} min="4" max="150" step="1" required />
                </div>
                <div className="form-group">
                  <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Credit Price Adjustment</label>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal', cursor: 'pointer', margin: 0 }}>
                      <input type="radio" name="createPricingMode" value="manual" checked={createPricingMode === 'manual'} onChange={() => setCreatePricingMode('manual')} style={{ width: 'auto', padding: 0, margin: 0 }} />
                      Manually Adjust Credit Price
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal', cursor: 'pointer', margin: 0 }}>
                      <input type="radio" name="createPricingMode" value="auto" checked={createPricingMode === 'auto'} onChange={() => setCreatePricingMode('auto')} style={{ width: 'auto', padding: 0, margin: 0 }} />
                      Auto-Adjust Credit Price
                    </label>
                  </div>
                  {createPricingMode === 'auto' && (
                    <div style={{ marginTop: '0.85rem', padding: '0.85rem 1rem', backgroundColor: '#f0f4ff', border: '1px solid #c0cff8', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                      <div>
                        <label style={{ fontSize: '0.82rem', color: '#555', display: 'block', marginBottom: '0.2rem' }}>First decrease in</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <input type="number" min="1" value={createAutoAdjust.firstDecreaseDays} onChange={e => setCreateAutoAdjust(p => ({ ...p, firstDecreaseDays: e.target.value }))} style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.9rem', width: '80px' }} />
                          <span style={{ fontSize: '0.9rem', color: '#555' }}>days from today</span>
                        </div>
                        {createAutoAdjust.firstDecreaseDays > 0 && (
                          <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.2rem' }}>
                            ({new Date(Date.now() + Number(createAutoAdjust.firstDecreaseDays) * 86400000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={{ fontSize: '0.82rem', color: '#555', display: 'block', marginBottom: '0.2rem' }}>Decrease percentage per interval</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <input type="number" min="1" max="99" value={createAutoAdjust.decreasePercent} onChange={e => setCreateAutoAdjust(p => ({ ...p, decreasePercent: e.target.value }))} style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.9rem', width: '80px' }} />
                          <span style={{ fontSize: '0.9rem', color: '#555' }}>%</span>
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.82rem', color: '#555', display: 'block', marginBottom: '0.2rem' }}>Repeat every</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <input type="number" min="1" value={createAutoAdjust.frequencyDays} onChange={e => setCreateAutoAdjust(p => ({ ...p, frequencyDays: e.target.value }))} style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.9rem', width: '80px' }} />
                          <span style={{ fontSize: '0.9rem', color: '#555' }}>days</span>
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize: '0.82rem', color: '#555', display: 'block', marginBottom: '0.2rem' }}>
                          Skip decrease if avg daily purchases (prior 3 days) exceeds <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span>
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <input type="number" min="1" value={createAutoAdjust.skipIfDailyAbove} onChange={e => setCreateAutoAdjust(p => ({ ...p, skipIfDailyAbove: e.target.value }))} placeholder="e.g. 100" style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.9rem', width: '100px' }} />
                          <span style={{ fontSize: '0.9rem', color: '#555' }}>purchases/day</span>
                        </div>
                      </div>
                      {createAutoAdjust.firstDecreaseDays > 0 && createAutoAdjust.decreasePercent && createAutoAdjust.frequencyDays && (
                        <div style={{ fontSize: '0.8rem', color: '#3366cc', marginTop: '0.25rem' }}>
                          Starting {new Date(Date.now() + Number(createAutoAdjust.firstDecreaseDays) * 86400000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}, price decreases {createAutoAdjust.decreasePercent}% that date and then {createAutoAdjust.decreasePercent}% every {createAutoAdjust.frequencyDays} day{createAutoAdjust.frequencyDays !== '1' ? 's' : ''} thereafter{createAutoAdjust.skipIfDailyAbove ? ` — skipped if avg daily purchases exceed ${createAutoAdjust.skipIfDailyAbove}` : ''}.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {error && (
                  <div style={{ padding: '0.65rem 0.9rem', marginBottom: '0.75rem', borderRadius: '6px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: '0.875rem', lineHeight: 1.45 }}>
                    {error}
                  </div>
                )}
                <button type="button" onClick={() => setShowDraftPreview(true)}>Preview</button>
                <button type="submit">Publish Article</button>
                <button type="button" onClick={(e) => handleSubmitArticle(e, true)} style={{ backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: '5px', padding: '0.4rem 0.85rem', fontWeight: '600', cursor: 'pointer', fontSize: '0.9rem' }}>Save as Draft</button>
                <button type="button" onClick={() => setShowForm(false)}>Cancel</button>
              </form>
            </div>
          ) : articles.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>
              <p>No articles published yet.</p>
            </div>
          ) : (
            <div className="article-feed">
              {/* Sort + Filter controls */}
              {(() => {
                const genreOptions = [...new Set(articles.map(a => a.genre_tree?.main).filter(Boolean))].sort();
                const authorOptions = [...new Set(articles.flatMap(a => a.writer_name || []))].sort();
                const hasFilters = filterGenre || filterAuthor || filterPricingMode || filterDateRange;
                return (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.6rem' }}>
                      <select
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value)}
                        style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.9rem', cursor: 'pointer' }}
                      >
                        <option value="date_desc">Date: Newest First</option>
                        <option value="date_asc">Date: Oldest First</option>
                        <option value="revenue_desc">Revenue: High → Low</option>
                        <option value="revenue_asc">Revenue: Low → High</option>
                        <option value="purchased_desc">Purchases: High → Low</option>
                        <option value="purchased_asc">Purchases: Low → High</option>
                        <option value="token_desc">Credit Price: High → Low</option>
                        <option value="token_asc">Credit Price: Low → High</option>
                      </select>

                      {genreOptions.length > 0 && (
                        <select
                          value={filterGenre}
                          onChange={e => setFilterGenre(e.target.value)}
                          style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: `1px solid ${filterGenre ? '#3366cc' : '#ccc'}`, fontSize: '0.9rem', cursor: 'pointer', backgroundColor: filterGenre ? '#f0f4ff' : '#fff' }}
                        >
                          <option value="">All Genres</option>
                          {genreOptions.map(g => <option key={g} value={g}>{g}</option>)}
                        </select>
                      )}

                      {authorOptions.length > 0 && (
                        <select
                          value={filterAuthor}
                          onChange={e => setFilterAuthor(e.target.value)}
                          style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: `1px solid ${filterAuthor ? '#3366cc' : '#ccc'}`, fontSize: '0.9rem', cursor: 'pointer', backgroundColor: filterAuthor ? '#f0f4ff' : '#fff' }}
                        >
                          <option value="">All Authors</option>
                          {authorOptions.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      )}

                      <select
                        value={filterPricingMode}
                        onChange={e => setFilterPricingMode(e.target.value)}
                        style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: `1px solid ${filterPricingMode ? '#3366cc' : '#ccc'}`, fontSize: '0.9rem', cursor: 'pointer', backgroundColor: filterPricingMode ? '#f0f4ff' : '#fff' }}
                      >
                        <option value="">All Pricing Modes</option>
                        <option value="manual">✏️ Manual</option>
                        <option value="auto">⚙️ Auto-Adjust</option>
                      </select>

                      <select
                        value={filterDateRange}
                        onChange={e => setFilterDateRange(e.target.value)}
                        style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: `1px solid ${filterDateRange ? '#3366cc' : '#ccc'}`, fontSize: '0.9rem', cursor: 'pointer', backgroundColor: filterDateRange ? '#f0f4ff' : '#fff' }}
                      >
                        <option value="">All Time</option>
                        <option value="last_30">Last 30 Days</option>
                        <option value="this_month">This Month</option>
                        <option value="this_week">This Week</option>
                        <option value="today">Today</option>
                      </select>

                      {hasFilters && (
                        <button
                          onClick={() => { setFilterGenre(''); setFilterAuthor(''); setFilterPricingMode(''); setFilterDateRange(''); }}
                          style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #dc3545', backgroundColor: '#fff', color: '#dc3545', fontSize: '0.85rem', cursor: 'pointer' }}
                        >
                          ✕ Clear Filters
                        </button>
                      )}
                    </div>

                    {(() => {
                      const now = new Date();
                      const todayStart  = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                      const weekStart   = (() => { const d = new Date(todayStart); d.setDate(d.getDate() - d.getDay()); return d; })();
                      const monthStart  = new Date(now.getFullYear(), now.getMonth(), 1);
                      const last30Start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                      const dateInRange = a => {
                        if (!filterDateRange) return true;
                        const d = new Date(a.date_created);
                        if (filterDateRange === 'today')      return d >= todayStart;
                        if (filterDateRange === 'this_week')  return d >= weekStart;
                        if (filterDateRange === 'this_month') return d >= monthStart;
                        if (filterDateRange === 'last_30')    return d >= last30Start;
                        return true;
                      };
                      const filtered = articles.filter(a =>
                        (!filterGenre || a.genre_tree?.main === filterGenre) &&
                        (!filterAuthor || (a.writer_name || []).includes(filterAuthor)) &&
                        (!filterPricingMode || a.pricingMode === filterPricingMode) &&
                        dateInRange(a)
                      );
                      if (!hasFilters) {
                        return (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: '#888' }}>
                            {articles.length} article{articles.length !== 1 ? 's' : ''}
                          </div>
                        );
                      }
                      const purchasedArticles = filtered.filter(a => (a.purchased || 0) > 0).length;
                      const subsetGross    = filtered.reduce((s, a) => s + (a.grossRevenue || 0), 0);
                      const subsetPurchases = filtered.reduce((s, a) => s + (a.purchased || 0), 0);
                      const pricingLabel = filterPricingMode === 'manual' ? 'Manual' : filterPricingMode === 'auto' ? 'Auto-Adjust' : '';
                      const dateLabel    = filterDateRange === 'today' ? 'Today' : filterDateRange === 'this_week' ? 'This Week' : filterDateRange === 'this_month' ? 'This Month' : filterDateRange === 'last_30' ? 'Last 30 Days' : '';
                      const labelParts = [filterGenre, filterAuthor, pricingLabel, dateLabel].filter(Boolean).join(' · ');
                      return (
                        <div style={{
                          marginTop: '0.75rem',
                          backgroundColor: '#f0f4ff',
                          border: '1px solid #c0cff8',
                          borderRadius: '8px',
                          padding: '0.75rem 1rem',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '1.25rem',
                          alignItems: 'center',
                        }}>
                          <div style={{ fontSize: '0.78rem', color: '#3366cc', fontWeight: '600', flexBasis: '100%', marginBottom: '-0.5rem' }}>
                            Filter: {labelParts}
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1a1a1a' }}>{filtered.length}</div>
                            <div style={{ fontSize: '0.72rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>of {articles.length} articles</div>
                          </div>
                          <div style={{ width: '1px', backgroundColor: '#c0cff8', alignSelf: 'stretch' }} />
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1a1a1a' }}>{purchasedArticles}</div>
                            <div style={{ fontSize: '0.72rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>articles purchased</div>
                          </div>
                          <div style={{ width: '1px', backgroundColor: '#c0cff8', alignSelf: 'stretch' }} />
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1a1a1a' }}>{subsetPurchases}</div>
                            <div style={{ fontSize: '0.72rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>total purchases</div>
                          </div>
                          <div style={{ width: '1px', backgroundColor: '#c0cff8', alignSelf: 'stretch' }} />
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1a7a3a' }}>${subsetGross.toFixed(2)}</div>
                            <div style={{ fontSize: '0.72rem', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' }}>revenue earned</div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              {(() => {
                const now2 = new Date();
                const todayStart2  = new Date(now2.getFullYear(), now2.getMonth(), now2.getDate());
                const weekStart2   = (() => { const d = new Date(todayStart2); d.setDate(d.getDate() - d.getDay()); return d; })();
                const monthStart2  = new Date(now2.getFullYear(), now2.getMonth(), 1);
                const last30Start2 = new Date(now2.getTime() - 30 * 24 * 60 * 60 * 1000);
                const dateInRange2 = a => {
                  if (!filterDateRange) return true;
                  const d = new Date(a.date_created);
                  if (filterDateRange === 'today')      return d >= todayStart2;
                  if (filterDateRange === 'this_week')  return d >= weekStart2;
                  if (filterDateRange === 'this_month') return d >= monthStart2;
                  if (filterDateRange === 'last_30')    return d >= last30Start2;
                  return true;
                };
                const filtered = [...articles]
                  .filter(a =>
                    (!filterGenre || a.genre_tree?.main === filterGenre) &&
                    (!filterAuthor || (a.writer_name || []).includes(filterAuthor)) &&
                    (!filterPricingMode || a.pricingMode === filterPricingMode) &&
                    dateInRange2(a)
                  )
                  .sort((a, b) => {
                    // Drafts always float to the top
                    if (a.status === 'draft' && b.status !== 'draft') return -1;
                    if (b.status === 'draft' && a.status !== 'draft') return 1;
                    switch (sortBy) {
                      case 'date_asc':       return new Date(a.date_created) - new Date(b.date_created);
                      case 'date_desc':      return new Date(b.date_created) - new Date(a.date_created);
                      case 'revenue_asc':    return (a.revenue ?? 0) - (b.revenue ?? 0);
                      case 'revenue_desc':   return (b.revenue ?? 0) - (a.revenue ?? 0);
                      case 'purchased_asc':  return (a.purchased ?? 0) - (b.purchased ?? 0);
                      case 'purchased_desc': return (b.purchased ?? 0) - (a.purchased ?? 0);
                      case 'token_asc':      return (a.token_cost ?? 0) - (b.token_cost ?? 0);
                      case 'token_desc':     return (b.token_cost ?? 0) - (a.token_cost ?? 0);
                      default: return 0;
                    }
                  });
                if (filtered.length === 0) {
                  return <div style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>No articles match the current filters.</div>;
                }
                return filtered.map((article) => {
                const isExpanded = expandedId === article._id;
                const articleUrl = `${window.location.origin}/hub-buy?article=${article._id}&company=${encodeURIComponent(company.company_name)}&title=${encodeURIComponent(article.title)}&price=${article.token_cost}`;
                const embedCode  = `<a href="${articleUrl}">PubSilo (${article.token_cost} Article Credits)</a>`;
                const priceFallback = article.token_cost === 0 ? 'Free' : `${article.token_cost} cr ($${(article.token_cost * 0.01).toFixed(2)})`;
                const priceWidgetSnippet = `<span data-pubsilo-id="${article._id}">${priceFallback}</span>\n\n<script>\n(function(){\n  var BASE='${window.location.origin}';\n  document.querySelectorAll('[data-pubsilo-id]').forEach(function(el){\n    var id=el.getAttribute('data-pubsilo-id');\n    if(!id) return;\n    fetch(BASE+'/api/embed/price/'+encodeURIComponent(id),{cache:'force-cache'})\n      .then(function(r){return r.ok?r.json():null})\n      .then(function(d){\n        if(!d) return;\n        el.textContent=d.token_cost===0?'Free':d.token_cost+' cr ($'+d.usd_price.toFixed(2)+')';\n      })\n      .catch(function(){});\n  });\n})();\n<\/script>`;
                const todayStr   = new Date().toISOString().split('T')[0];

                const isDraft = article.status === 'draft';
                return (
                  <div key={article._id} className="article-card" style={{ cursor: 'pointer', borderLeft: isDraft ? '4px solid #dc3545' : article.status === 'pending' ? '4px solid #e67e22' : article.status === 'rejected' ? '4px solid #dc3545' : undefined, backgroundColor: isDraft ? '#fff5f5' : undefined }}>
                    {/* Clickable header row */}
                    <div
                      className="article-header"
                      onClick={() => toggleDropdown(article._id)}
                      style={{ userSelect: 'none', display: 'flex', alignItems: 'flex-start' }}
                    >
                      <div style={{ flex: 1 }}>
                        {article.genre_tree?.main && (
                          <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: '0.2rem', letterSpacing: '0.03em' }}>
                            {article.genre_tree.main}{article.genre_tree.sub ? ` / ${article.genre_tree.sub.split(' › ').join(' / ')}` : ''}
                          </div>
                        )}
                        <h3 className="article-title" style={{ margin: 0 }}>{article.title}</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.82rem', color: '#555', marginTop: '0.3rem' }}>
                          <span>Written By: <strong>{Array.isArray(article.writer_name) ? article.writer_name.join(', ') : article.writer_name}</strong></span>
                          <span>Published: {new Date(article.date_created).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0, marginTop: '0.1rem' }}>
                        {article.status && article.status !== 'approved' && (
                          <span style={{ fontSize: '0.72rem', fontWeight: '700', color: article.status === 'pending' ? '#e67e22' : '#dc3545', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                            {article.status === 'draft' ? 'DRAFT' : article.status === 'pending' ? 'Pending Approval' : 'Rejected'}
                          </span>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); setGraphArticle({ _id: article._id, title: article.title }); }}
                          title="View purchase history graph"
                          style={{ padding: '0.2rem 0.55rem', fontSize: '0.78rem', backgroundColor: '#f0f4ff', color: '#3366cc', border: '1px solid #c0cff8', borderRadius: '5px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          📈 Graph
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); setPreviewArticle(article); }}
                          title="Preview article"
                          style={{ padding: '0.2rem 0.55rem', fontSize: '0.78rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '5px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          Preview
                        </button>
                        <span style={{ fontSize: '0.8rem', color: '#aaa' }}>
                          {isExpanded ? '▲' : '▼'}
                        </span>
                      </div>
                    </div>

                    <div className="article-meta" onClick={() => toggleDropdown(article._id)}>
                      <span className="purchases">📖 {article.purchased} access{article.purchased !== 1 ? 'es' : ''}</span>
                      <span style={{ fontSize: '0.82rem', color: '#555' }}>💳 {article.token_cost} cr</span>
                      {article.lastPurchasedAt && (
                        <span style={{ fontSize: '0.75rem', color: '#888' }}>Last accessed {localDateDisplay(article.lastPurchasedAt)}</span>
                      )}
                      {article.kudos > 0 && <span style={{ fontSize: '0.82rem', color: '#7b2ff7' }}>👍 {article.kudos}</span>}
                      {article.returns > 0 && (
                        <span style={{ fontSize: '0.82rem', color: '#dc3545' }}>
                          ↩ {article.returns} return{article.returns !== 1 ? 's' : ''}
                        </span>
                      )}
                      {(() => {
                        const readers = liveReaders[article._id] || 0;
                        return readers > 0 ? (
                          <span style={{ fontSize: '0.82rem', color: '#28a745' }}>
                            🟢 {readers} reading now
                          </span>
                        ) : null;
                      })()}
                      <span style={{ color: '#28a745', fontWeight: '600', fontSize: '0.85rem' }}>
                        💰 ${(article.grossRevenue || article.revenue || 0).toFixed(2)} earned
                      </span>
                    </div>

                    {/* Pricing mode badge */}
                    {!isExpanded && (
                      <div style={{ textAlign: 'right', marginTop: '0.3rem', fontSize: '0.72rem', color: article.pricingMode === 'auto' ? '#3366cc' : '#888', fontStyle: 'italic' }}
                        onClick={() => toggleDropdown(article._id)}>
                        {article.pricingMode === 'auto' ? '⚙️ Auto Credit Adjustment' : '✏️ Manual Credit Adjustment'}
                      </div>
                    )}


                    {/* Dropdown panel */}
                    {isExpanded && (
                      <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        backgroundColor: isDraft ? '#fff0f0' : article.status === 'pending' ? '#fff8f0' : article.status === 'rejected' ? '#fff0f0' : '#f8f9fa',
                        border: `1px solid ${isDraft ? '#f5c6cb' : article.status === 'pending' ? '#f5d9b0' : article.status === 'rejected' ? '#f5c6cb' : '#e0e0e0'}`,
                        borderRadius: '8px',
                      }}>

                        {/* Article stats summary */}
                        <div style={{
                          display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.25rem',
                          marginBottom: '1rem', paddingBottom: '0.75rem',
                          borderBottom: `1px solid ${isDraft ? '#f5c6cb' : '#e0e0e0'}`,
                          fontSize: '0.8rem', color: '#555',
                        }}>
                          <span>📅 Published: <strong>{new Date(article.date_created).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</strong></span>
                          <span>📖 <strong>{article.purchased || 0}</strong> total access{(article.purchased || 0) !== 1 ? 'es' : ''}</span>
                          <span style={{ color: article.lastPurchasedAt ? '#333' : '#aaa' }}>
                            🕐 Last licensed:{' '}
                            <strong>
                              {article.lastPurchasedAt
                                ? localDateDisplay(article.lastPurchasedAt)
                                : article.purchased > 0 ? 'Before tracking began' : 'Never'}
                            </strong>
                          </span>
                        </div>

                        {/* Publish Draft action */}
                        {isDraft && (
                          <div style={{ marginBottom: '1.25rem' }}>
                            <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#721c24', marginBottom: '0.5rem' }}>📋 Draft Article</div>
                            <p style={{ fontSize: '0.82rem', color: '#555', marginBottom: '0.75rem' }}>This article is saved as a draft and is not visible to readers. Publish it to make it live.</p>
                            <button
                              onClick={() => handlePublishDraft(article._id)}
                              style={{ padding: '0.4rem 1rem', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}
                            >
                              ✅ Publish Now
                            </button>
                          </div>
                        )}

                        {/* Pending approval — company can approve or return to draft */}
                        {article.status === 'pending' && (
                          <div style={{ marginBottom: '1.25rem' }}>
                            <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#7d4a00', marginBottom: '0.5rem' }}>⏳ Pending Editorial Review</div>
                            <p style={{ fontSize: '0.82rem', color: '#555', marginBottom: '0.75rem' }}>This article was submitted by an author and is waiting for editorial approval. As the publisher, you can approve it directly or return it to draft for revision.</p>
                            <div style={{ display: 'flex', gap: '0.6rem' }}>
                              <button
                                onClick={() => handlePublishDraft(article._id)}
                                style={{ padding: '0.4rem 1rem', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}
                              >
                                ✅ Approve &amp; Publish
                              </button>
                              <button
                                onClick={() => handleReturnToDraft(article._id)}
                                style={{ padding: '0.4rem 1rem', backgroundColor: '#fff', color: '#555', border: '1px solid #ccc', borderRadius: '6px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}
                              >
                                ↩ Return to Draft
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Rejected — company can return to draft for revision */}
                        {article.status === 'rejected' && (
                          <div style={{ marginBottom: '1.25rem' }}>
                            <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#721c24', marginBottom: '0.5rem' }}>❌ Rejected by Editor</div>
                            <p style={{ fontSize: '0.82rem', color: '#555', marginBottom: '0.75rem' }}>This article was rejected. Return it to draft so the author can revise and resubmit, or approve it directly to override the rejection.</p>
                            <div style={{ display: 'flex', gap: '0.6rem' }}>
                              <button
                                onClick={() => handleReturnToDraft(article._id)}
                                style={{ padding: '0.4rem 1rem', backgroundColor: '#fff', color: '#555', border: '1px solid #ccc', borderRadius: '6px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}
                              >
                                ↩ Return to Draft
                              </button>
                              <button
                                onClick={() => handlePublishDraft(article._id)}
                                style={{ padding: '0.4rem 1rem', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}
                              >
                                ✅ Approve Anyway
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Get Link */}
                        <div style={{ marginBottom: '1.25rem' }}>
                          <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.4rem' }}>🔗 Get Link</div>
                          <div style={{ fontSize: '0.82rem', color: '#555', marginBottom: '0.35rem' }}>Article URL:</div>
                          <div style={{
                            backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '5px',
                            padding: '0.45rem 0.75rem', fontFamily: 'monospace', fontSize: '0.82rem',
                            wordBreak: 'break-all', userSelect: 'all', marginBottom: '0.6rem',
                          }}>
                            {articleUrl}
                          </div>
                          <div style={{ fontSize: '0.82rem', color: '#555', marginBottom: '0.35rem' }}>Embed code:</div>
                          <div style={{
                            backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '5px',
                            padding: '0.45rem 0.75rem', fontFamily: 'monospace', fontSize: '0.82rem',
                            wordBreak: 'break-all', userSelect: 'all',
                          }}>
                            {embedCode}
                          </div>
                          <div style={{ marginTop: '0.85rem' }}>
                            <div style={{ fontWeight: '600', fontSize: '0.82rem', color: '#555', marginBottom: '0.3rem' }}>📡 Live Price Widget</div>
                            <div style={{ fontSize: '0.78rem', color: '#666', marginBottom: '0.75rem' }}>
                              Shows the current token price on your site and auto-updates when pricing changes.
                            </div>

                            {/* Span — one per article */}
                            <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                              Step 1 — paste this span where you want the price to appear
                            </div>
                            <div style={{
                              backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '5px',
                              padding: '0.45rem 0.75rem', fontFamily: 'monospace', fontSize: '0.78rem',
                              wordBreak: 'break-all', userSelect: 'all', marginBottom: '0.3rem',
                            }}>
                              {`<span data-pubsilo-id="${article._id}">${priceFallback}</span>`}
                            </div>
                            <button
                              onClick={() => navigator.clipboard.writeText(`<span data-pubsilo-id="${article._id}">${priceFallback}</span>`)}
                              style={{ width: '100%', padding: '0.3rem', fontSize: '0.78rem', backgroundColor: '#e8f4fd', color: '#1a6fc7', border: '1px solid #d0e4f5', borderRadius: '5px', cursor: 'pointer', marginBottom: '0.75rem' }}
                            >
                              Copy Span
                            </button>

                            {/* Script — once per page */}
                            <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                              Step 2 — paste this script once per page, before &lt;/body&gt;
                            </div>
                            <pre style={{
                              backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '5px',
                              padding: '0.45rem 0.75rem', fontFamily: 'monospace', fontSize: '0.72rem',
                              whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: '0 0 0.3rem', userSelect: 'all',
                            }}>{`<script>
(function(){
  var BASE='${window.location.origin}';
  document.querySelectorAll('[data-pubsilo-id]').forEach(function(el){
    var id=el.getAttribute('data-pubsilo-id');
    if(!id) return;
    fetch(BASE+'/api/embed/price/'+encodeURIComponent(id),{cache:'force-cache'})
      .then(function(r){return r.ok?r.json():null})
      .then(function(d){
        if(!d) return;
        el.textContent=d.token_cost===0?'Free':d.token_cost+' cr ($'+d.usd_price.toFixed(2)+')';
      })
      .catch(function(){});
  });
})();
</script>`}</pre>
                            <button
                              onClick={() => navigator.clipboard.writeText(`<script>\n(function(){\n  var BASE='${window.location.origin}';\n  document.querySelectorAll('[data-pubsilo-id]').forEach(function(el){\n    var id=el.getAttribute('data-pubsilo-id');\n    if(!id) return;\n    fetch(BASE+'/api/embed/price/'+encodeURIComponent(id),{cache:'force-cache'})\n      .then(function(r){return r.ok?r.json():null})\n      .then(function(d){\n        if(!d) return;\n        el.textContent=d.token_cost===0?'Free':d.token_cost+' cr ($'+d.usd_price.toFixed(2)+')';\n      })\n      .catch(function(){});\n  });\n})();\n<\/script>`)}
                              style={{ width: '100%', padding: '0.3rem', fontSize: '0.78rem', backgroundColor: '#e8f4fd', color: '#1a6fc7', border: '1px solid #d0e4f5', borderRadius: '5px', cursor: 'pointer' }}
                            >
                              Copy Script
                            </button>
                            <div style={{ fontSize: '0.72rem', color: '#999', marginTop: '0.35rem' }}>
                              The script handles all spans on the page — only add it once even if you have multiple articles.
                            </div>
                          </div>
                        </div>

                        <hr style={{ margin: '0.75rem 0', borderColor: '#ddd' }} />

                        {/* Change Token Price + History */}
                        <div onClick={e => e.stopPropagation()} style={{ marginBottom: '1.25rem' }}>
                          <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.6rem' }}>💲 Credit Price</div>
                          <div style={{ fontSize: '0.88rem', color: '#333', marginBottom: article.pricingMode === 'manual' ? '0.5rem' : 0 }}>
                            Current: <strong>{article.token_cost} Article Credits</strong>
                          </div>
                          {article.pricingMode !== 'auto' && (
                            <>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                                <input
                                  type="number"
                                  min="4"
                                  max="150"
                                  step="1"
                                  placeholder="New price"
                                  value={priceChangeForm}
                                  onChange={e => setPriceChangeForm(e.target.value)}
                                  style={{ padding: '0.3rem 0.55rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.85rem', width: '100px' }}
                                />
                                <button
                                  onClick={() => handlePriceChange(article)}
                                  disabled={priceChangeLoading || priceChangeForm === ''}
                                  style={{ padding: '0.3rem 0.75rem', backgroundColor: '#343a40', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.85rem', cursor: 'pointer', opacity: priceChangeForm === '' ? 0.5 : 1 }}
                                >
                                  {priceChangeLoading ? 'Saving…' : 'Update Price'}
                                </button>
                              </div>
                              {priceChangeMsg && (
                                <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: priceChangeMsg.startsWith('Price updated') ? '#28a745' : '#dc3545' }}>
                                  {priceChangeMsg}
                                </div>
                              )}
                            </>
                          )}

                          {/* Price History Table */}
                          {(article.priceHistory || []).length > 0 && (() => {
                            const logMap = {};
                            (article.purchaseLog || []).forEach(e => {
                              if (!logMap[e.token_cost]) logMap[e.token_cost] = { count: 0, promoTokensUsed: 0 };
                              logMap[e.token_cost].count           += e.count || 0;
                              logMap[e.token_cost].promoTokensUsed += e.promoTokensUsed || 0;
                            });
                            return (
                            <div style={{ marginTop: '0.85rem' }}>
                              <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#555', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Price History &amp; Purchases</div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                                    <th style={thStyle}>Credits</th>
                                    <th style={thStyle}>From</th>
                                    <th style={thStyle}>To</th>
                                    <th style={thStyle}>Purchases</th>
                                    <th style={{ ...thStyle, color: '#7c3aed' }}>Promo 🪙</th>
                                    <th style={thStyle}>Revenue</th>
                                    <th style={thStyle}>By</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...article.priceHistory].reverse().map((h, i) => {
                                    const entry       = logMap[h.token_cost] || { count: 0, promoTokensUsed: 0 };
                                    const purchases   = entry.count;
                                    const promoUsed   = entry.promoTokensUsed;
                                    const netTokens   = Math.max(0, purchases * h.token_cost - promoUsed);
                                    const revenue     = (netTokens * 0.01).toFixed(2);
                                    return (
                                      <tr key={i} style={{ backgroundColor: h.endedAt === null ? '#f0fff4' : '#fff', borderBottom: '1px solid #eee' }}>
                                        <td style={tdStyle}>
                                          <strong>{h.token_cost}</strong>
                                          {h.endedAt === null && <span style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#28a745', fontWeight: '600' }}>current</span>}
                                        </td>
                                        <td style={tdStyle}>{new Date(h.startedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                                        <td style={tdStyle}>{h.endedAt ? new Date(h.endedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                                        <td style={{ ...tdStyle, fontWeight: purchases > 0 ? '600' : 'normal', color: purchases > 0 ? '#1a1a1a' : '#aaa' }}>{purchases > 0 ? purchases.toLocaleString() : '—'}</td>
                                        <td style={{ ...tdStyle, color: promoUsed > 0 ? '#7c3aed' : '#aaa', fontWeight: promoUsed > 0 ? '600' : 'normal' }}>{promoUsed > 0 ? `−${promoUsed}` : '—'}</td>
                                        <td style={{ ...tdStyle, color: netTokens > 0 ? '#1a7a3a' : '#aaa' }}>{purchases > 0 ? `$${revenue}` : '—'}</td>
                                        <td style={tdStyle}>{h.changedBy || 'manual'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            );
                          })()}
                        </div>

                        <hr style={{ margin: '0.75rem 0', borderColor: '#ddd' }} />

                        {/* Credit Price Adjustment (auto-schedule) */}
                        <div onClick={e => e.stopPropagation()}>
                          <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.6rem' }}>📉 Credit Price Adjustment</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', marginBottom: '0.75rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.88rem' }}>
                              <input type="radio" name={`pricingMode-${article._id}`} value="manual" checked={pricingForm.mode === 'manual'} onChange={() => setPricingForm(f => ({ ...f, mode: 'manual' }))} />
                              Manually Adjust Credit Price
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.88rem' }}>
                              <input type="radio" name={`pricingMode-${article._id}`} value="auto" checked={pricingForm.mode === 'auto'} onChange={() => setPricingForm(f => ({ ...f, mode: 'auto' }))} />
                              Auto-Adjust Credit Price
                            </label>
                          </div>

                          {pricingForm.mode === 'auto' && (
                            <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f0f4ff', border: '1px solid #c0cff8', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.75rem' }}>
                              <div>
                                <div style={{ fontSize: '0.78rem', color: '#555', marginBottom: '0.2rem' }}>First decrease in</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  <input type="number" min="1" value={pricingForm.firstDecreaseDays} onChange={e => setPricingForm(f => ({ ...f, firstDecreaseDays: e.target.value }))} style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.85rem', width: '80px' }} />
                                  <span style={{ fontSize: '0.85rem', color: '#555' }}>days from today</span>
                                </div>
                                {pricingForm.firstDecreaseDays > 0 && (
                                  <div style={{ fontSize: '0.76rem', color: '#888', marginTop: '0.2rem' }}>
                                    ({new Date(Date.now() + Number(pricingForm.firstDecreaseDays) * 86400000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})
                                  </div>
                                )}
                              </div>
                              <div>
                                <div style={{ fontSize: '0.78rem', color: '#555', marginBottom: '0.2rem' }}>Decrease percentage per interval</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  <input type="number" min="1" max="99" value={pricingForm.decreasePercent} onChange={e => setPricingForm(f => ({ ...f, decreasePercent: e.target.value }))} style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.85rem', width: '80px' }} />
                                  <span style={{ fontSize: '0.85rem', color: '#555' }}>%</span>
                                </div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.78rem', color: '#555', marginBottom: '0.2rem' }}>Repeat every</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  <input type="number" min="1" value={pricingForm.frequencyDays} onChange={e => setPricingForm(f => ({ ...f, frequencyDays: e.target.value }))} style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.85rem', width: '80px' }} />
                                  <span style={{ fontSize: '0.85rem', color: '#555' }}>days</span>
                                </div>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.78rem', color: '#555', marginBottom: '0.2rem' }}>
                                  Skip decrease if avg daily purchases (prior 3 days) exceeds <span style={{ color: '#888' }}>(optional)</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  <input type="number" min="1" value={pricingForm.skipIfDailyAbove} onChange={e => setPricingForm(f => ({ ...f, skipIfDailyAbove: e.target.value }))} placeholder="e.g. 100" style={{ padding: '0.35rem 0.6rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.85rem', width: '100px' }} />
                                  <span style={{ fontSize: '0.85rem', color: '#555' }}>purchases/day</span>
                                </div>
                              </div>
                              {pricingForm.firstDecreaseDays > 0 && pricingForm.decreasePercent && pricingForm.frequencyDays && (
                                <div style={{ fontSize: '0.79rem', color: '#3366cc' }}>
                                  Starting {new Date(Date.now() + Number(pricingForm.firstDecreaseDays) * 86400000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}, price decreases {pricingForm.decreasePercent}% that date and then {pricingForm.decreasePercent}% every {pricingForm.frequencyDays} day{pricingForm.frequencyDays !== '1' ? 's' : ''} thereafter{pricingForm.skipIfDailyAbove ? ` — skipped if avg daily purchases exceed ${pricingForm.skipIfDailyAbove}` : ''}.
                                </div>
                              )}
                            </div>
                          )}

                          <button
                            onClick={() => handlePricingSubmit(article)}
                            disabled={pricingLoading || (pricingForm.mode === 'auto' && (!pricingForm.firstDecreaseDays || !pricingForm.decreasePercent || !pricingForm.frequencyDays))}
                            style={{ padding: '0.35rem 0.9rem', backgroundColor: '#343a40', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem', opacity: (pricingForm.mode === 'auto' && (!pricingForm.firstDecreaseDays || !pricingForm.decreasePercent || !pricingForm.frequencyDays)) ? 0.5 : 1 }}
                          >
                            {pricingLoading ? 'Saving...' : 'Save Pricing Settings'}
                          </button>
                          {pricingMsg && (
                            <div style={{ marginTop: '0.5rem', fontSize: '0.82rem', color: pricingMsg.startsWith('Failed') ? '#dc3545' : '#28a745' }}>
                              {pricingMsg}
                            </div>
                          )}
                        </div>

                        <hr style={{ margin: '0.75rem 0', borderColor: '#ecc' }} />

                        {/* Sunset Date */}
                        <div style={{ marginBottom: '0.75rem' }}>
                          <div style={{ fontWeight: '600', fontSize: '0.82rem', color: '#555', marginBottom: '0.25rem' }}>
                            🌅 Content Access Sunset
                          </div>
                          <div style={{ fontSize: '0.78rem', color: '#777', marginBottom: '0.4rem', lineHeight: 1.45 }}>
                            Choose a future date to close this article to new readers. Readers who have already licensed it keep full access — no one loses anything. After the sunset date, the article is removed from all public feeds and no new licensing is accepted.
                          </div>
                          {article.sunsetDate ? (
                            <div style={{ fontSize: '0.8rem', color: '#664d03', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '5px', padding: '0.4rem 0.6rem', marginBottom: '0.4rem' }}>
                              Closes to new licensing on {localDateDisplay(article.sunsetDate)}
                            </div>
                          ) : (
                            <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: '0.4rem' }}>No sunset set — open for new licensing indefinitely.</div>
                          )}
                          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                              type="date"
                              min={tomorrowYMD}
                              value={sunsetEdit[article._id] ?? (article.sunsetDate ? localDateYMD(article.sunsetDate) : '')}
                              onChange={e => setSunsetEdit(s => ({ ...s, [article._id]: e.target.value }))}
                              style={{ padding: '0.25rem 0.4rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.8rem' }}
                            />
                            <button onClick={() => handleSunsetSave(article._id, sunsetEdit[article._id] || '')} style={{ padding: '0.25rem 0.6rem', background: '#343a40', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.78rem', cursor: 'pointer' }}>
                              Set
                            </button>
                            {article.sunsetDate && (
                              <button onClick={() => { setSunsetEdit(s => ({ ...s, [article._id]: '' })); handleSunsetSave(article._id, ''); }} style={{ padding: '0.25rem 0.6rem', background: '#fff', color: '#666', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.78rem', cursor: 'pointer' }}>
                                Clear
                              </button>
                            )}
                          </div>
                          {sunsetMsg[article._id] && (
                            <div style={{ fontSize: '0.78rem', marginTop: '0.3rem', color: sunsetMsg[article._id].startsWith('Failed') ? '#dc3545' : '#198754' }}>
                              {sunsetMsg[article._id]}
                            </div>
                          )}
                        </div>

                        <hr style={{ margin: '0.75rem 0', borderColor: '#ecc' }} />

                        {/* Delete Article */}
                        <div style={{ marginTop: '0.5rem' }}>
                          <button
                            onClick={() => setDeleteTarget({ _id: article._id, title: article.title, purchased: article.purchased || 0, lastPurchasedAt: article.lastPurchasedAt || null })}
                            style={{ padding: '0.4rem 0.85rem', backgroundColor: '#fff', color: '#cc0000', border: '1.5px solid #cc0000', borderRadius: '6px', fontWeight: '600', fontSize: '0.82rem', cursor: 'pointer' }}
                          >
                            🗑️ Delete Article
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
              })()}
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div style={{ width: '220px', flexShrink: 0, borderLeft: `1px solid ${T.border}`, minHeight: 'calc(100vh - 146px)', padding: '16px 12px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#888', paddingBottom: '8px', borderBottom: `2px solid ${T.accent}`, marginBottom: '12px', fontFamily: 'Arial, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Quick Actions</span>
            <div style={{ position: 'relative' }}>
              <span
                onClick={() => { setSettingsGearOpen(o => !o); if (settingsGearOpen) setSettingsView(null); }}
                style={{ fontSize: '1.2rem', cursor: 'pointer', color: '#888', userSelect: 'none', padding: '0 0.2rem' }}
                title="Settings"
              >⚙️</span>
              {settingsGearOpen && (
                <div style={{ position: 'absolute', right: 0, top: '110%', zIndex: 300, background: '#fff', border: '1px solid #ddd', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.13)', minWidth: '170px', padding: '0.25rem 0' }}>
                  <div
                    onClick={() => { setSettingsView(settingsView === 'logo' ? null : 'logo'); setSettingsGearOpen(false); }}
                    style={{ padding: '0.5rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', color: '#333' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >🖼️ Upload Logo Banner</div>
                  <div
                    onClick={() => { setSettingsView(settingsView === 'embed' ? null : 'embed'); setSettingsGearOpen(false); }}
                    style={{ padding: '0.5rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', color: '#333' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >🔗 External Embed</div>
                  <div
                    onClick={() => { setSettingsView(settingsView === 'w9' ? null : 'w9'); setSettingsGearOpen(false); setW9Msg(''); }}
                    style={{ padding: '0.5rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', color: '#333' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    📋 Tax Info / W-9
                    {!company?.w9?.submitted && <span style={{ marginLeft: '0.35rem', fontSize: '0.7rem', color: '#c0710a', fontWeight: 700 }}>Required</span>}
                    {company?.w9?.submitted  && <span style={{ marginLeft: '0.35rem', fontSize: '0.7rem', color: '#28a745' }}>✓ On file</span>}
                  </div>
                  <div
                    onClick={() => { setSettingsView(settingsView === 'billing' ? null : 'billing'); setSettingsGearOpen(false); setBillingEmailMsg(''); setBillingEmailVal(company?.billingEmail || ''); }}
                    style={{ padding: '0.5rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', color: '#333' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >💳 Billing / Invoice Email</div>
                  <div style={{ height: '1px', background: '#eee', margin: '0.2rem 0' }} />
                  <div
                    onClick={() => { setSupportOpen(true); setSupportTopic(''); setSupportMsg(''); setSupportResult(null); setSupportErr(''); setSettingsGearOpen(false); }}
                    style={{ padding: '0.5rem 0.85rem', fontSize: '0.82rem', cursor: 'pointer', color: '#333' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >📧 Contact Support</div>
                </div>
              )}
            </div>
          </div>

          {/* Settings panels — shown inline below Quick Actions */}
          {settingsView === 'logo' && (
            <div style={{ marginBottom: '1rem', padding: '0.85rem', backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <div style={{ fontWeight: '600', fontSize: '0.82rem', marginBottom: '0.6rem' }}>🖼️ Company Logo Banner</div>
              {company?.hero_image && (
                <img src={company.hero_image} alt="Current logo" style={{ width: '100%', height: '60px', objectFit: 'contain', borderRadius: '6px', marginBottom: '0.6rem', border: '1px solid #ddd', backgroundColor: '#111' }} />
              )}
              <label style={{ display: 'block', padding: '0.4rem 0.75rem', backgroundColor: '#1a1a1a', color: '#fff', borderRadius: '6px', fontSize: '0.82rem', cursor: 'pointer', textAlign: 'center' }}>
                {logoUploading ? 'Uploading…' : company?.hero_image ? 'Replace Image' : 'Choose Image'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} disabled={logoUploading} />
              </label>
              {logoMsg && <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: logoMsg === 'Logo updated.' ? '#28a745' : '#dc3545' }}>{logoMsg}</div>}
              <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: '#aaa' }}>Max 3 MB. Displays as banner on your company silo and in user article feeds.</div>
            </div>
          )}

          {/* W-9 / Tax Information panel */}
          {settingsView === 'w9' && (
            <div style={{ marginBottom: '1rem', padding: '0.85rem', backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <div style={{ fontWeight: '700', fontSize: '0.84rem', marginBottom: '0.5rem', color: '#111' }}>📋 Tax Information (W-9)</div>

              {company?.w9?.submitted ? (
                <div>
                  <div style={{ fontSize: '0.78rem', color: '#28a745', fontWeight: 700, marginBottom: '0.4rem' }}>✓ W-9 on file</div>
                  <div style={{ fontSize: '0.75rem', color: '#555', lineHeight: 1.7 }}>
                    <div><strong>Legal name:</strong> {company.w9.legalName}</div>
                    {company.w9.businessName && <div><strong>Business name:</strong> {company.w9.businessName}</div>}
                    <div><strong>Classification:</strong> {company.w9.taxClassification?.replace(/_/g, ' ') || '—'}{company.w9.taxClassification === 'llc' && company.w9.llcMemberType ? ` (taxed as ${company.w9.llcMemberType === 'P' ? 'Partnership/DE' : company.w9.llcMemberType + ' Corp'})` : ''}</div>
                    <div><strong>TIN type:</strong> {company.w9.tinType?.toUpperCase() || '—'}</div>
                    <div><strong>Submitted:</strong> {company.w9.submittedAt ? new Date(company.w9.submittedAt).toLocaleDateString() : '—'}</div>
                  </div>
                  <div style={{ marginTop: '0.5rem', fontSize: '0.71rem', color: '#aaa' }}>To update your W-9 information, contact support.</div>
                </div>
              ) : (
                <form onSubmit={handleW9Submit}>
                  <div style={{ fontSize: '0.72rem', color: '#c0710a', fontWeight: 700, marginBottom: '0.6rem', padding: '0.4rem 0.6rem', background: '#fff8e6', border: '1px solid #f0d080', borderRadius: '5px' }}>
                    Required for licensing payments. Needed for IRS 1099-NEC reporting when applicable.
                  </div>

                  {[
                    { label: 'Legal Name *', key: 'legalName', placeholder: 'Name as on tax return', hint: 'Full legal name or entity name' },
                    { label: 'Business / DBA Name', key: 'businessName', placeholder: 'DBA name (if different)', hint: 'Leave blank if same as legal name' },
                    { label: 'Street Address *', key: 'address', placeholder: '123 Main St' },
                    { label: 'City, State, ZIP *', key: 'cityStateZip', placeholder: 'Tulsa, OK 74103' },
                  ].map(({ label, key, placeholder, hint }) => (
                    <div key={key} style={{ marginBottom: '0.45rem' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', marginBottom: '2px' }}>{label}</div>
                      <input
                        type="text"
                        value={w9Form[key]}
                        onChange={e => setW9Form(f => ({ ...f, [key]: e.target.value }))}
                        placeholder={placeholder}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '0.35rem 0.55rem', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.78rem' }}
                      />
                      {hint && <div style={{ fontSize: '0.66rem', color: '#888', marginTop: '2px' }}>{hint}</div>}
                    </div>
                  ))}

                  <div style={{ marginBottom: '0.45rem' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', marginBottom: '2px' }}>Tax Classification *</div>
                    <select
                      value={w9Form.taxClassification}
                      onChange={e => setW9Form(f => ({ ...f, taxClassification: e.target.value, llcMemberType: '' }))}
                      style={{ width: '100%', padding: '0.35rem 0.55rem', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.78rem' }}
                    >
                      <option value="">Select…</option>
                      <option value="individual_sole_proprietor">Individual / Sole Proprietor</option>
                      <option value="c_corp">C Corporation</option>
                      <option value="s_corp">S Corporation</option>
                      <option value="partnership">Partnership</option>
                      <option value="trust_estate">Trust / Estate</option>
                      <option value="llc">LLC</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {w9Form.taxClassification === 'llc' && (
                    <div style={{ marginBottom: '0.45rem' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', marginBottom: '2px' }}>LLC Tax Treatment *</div>
                      <select
                        value={w9Form.llcMemberType}
                        onChange={e => setW9Form(f => ({ ...f, llcMemberType: e.target.value }))}
                        style={{ width: '100%', padding: '0.35rem 0.55rem', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.78rem' }}
                      >
                        <option value="">Select…</option>
                        <option value="C">C Corporation</option>
                        <option value="S">S Corporation</option>
                        <option value="P">Partnership / Disregarded Entity</option>
                      </select>
                      <div style={{ fontSize: '0.66rem', color: '#888', marginTop: '2px' }}>How the LLC is taxed (required by IRS W-9 Line 3)</div>
                    </div>
                  )}

                  <div style={{ marginBottom: '0.45rem' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', marginBottom: '2px' }}>TIN Type *</div>
                    <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.78rem' }}>
                      {[['ssn', 'SSN'], ['ein', 'EIN']].map(([val, lbl]) => (
                        <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }}>
                          <input type="radio" name="tinType" value={val} checked={w9Form.tinType === val} onChange={() => setW9Form(f => ({ ...f, tinType: val }))} />
                          {lbl}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: '0.6rem' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', marginBottom: '2px' }}>
                      {w9Form.tinType === 'ein' ? 'EIN (XX-XXXXXXX)' : 'SSN (XXX-XX-XXXX)'} *
                    </div>
                    <input
                      type="password"
                      value={w9Form.tin}
                      onChange={e => setW9Form(f => ({ ...f, tin: e.target.value }))}
                      placeholder="9 digits, no dashes"
                      maxLength={11}
                      style={{ width: '100%', boxSizing: 'border-box', padding: '0.35rem 0.55rem', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.78rem' }}
                    />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', fontSize: '0.72rem', color: '#444', marginBottom: '0.6rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={w9Form.certified} onChange={e => setW9Form(f => ({ ...f, certified: e.target.checked }))} style={{ marginTop: '2px', flexShrink: 0 }} />
                    Under penalties of perjury, I certify that the information provided is true, correct, and complete, and that I am not subject to backup withholding.
                  </label>

                  {w9Msg && w9Msg !== 'success' && (
                    <div style={{ fontSize: '0.75rem', color: '#dc3545', marginBottom: '0.5rem' }}>{w9Msg}</div>
                  )}
                  {w9Msg === 'success' && (
                    <div style={{ fontSize: '0.75rem', color: '#28a745', marginBottom: '0.5rem', fontWeight: 700 }}>✓ W-9 submitted successfully.</div>
                  )}

                  <button
                    type="submit"
                    disabled={w9Loading}
                    style={{ width: '100%', padding: '0.4rem', backgroundColor: w9Loading ? '#aaa' : '#0d3165', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.78rem', fontWeight: 700, cursor: w9Loading ? 'not-allowed' : 'pointer' }}
                  >
                    {w9Loading ? 'Submitting…' : 'Submit W-9'}
                  </button>

                  <div style={{ marginTop: '0.45rem', fontSize: '0.68rem', color: '#aaa' }}>
                    Your TIN is transmitted securely and stored encrypted. It is never displayed in full after submission. PubSilo collects this information solely for IRS tax reporting.
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Billing / Invoice Email panel */}
          {settingsView === 'billing' && (
            <div style={{ marginBottom: '1rem', padding: '0.85rem', backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '8px' }}>
              <div style={{ fontWeight: '700', fontSize: '0.84rem', marginBottom: '0.4rem', color: '#111' }}>💳 Billing / Invoice Email</div>
              <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.65rem', lineHeight: 1.5 }}>
                Monthly invoices are sent to this address. If left blank, invoices go to your account email (<strong>{company?.email}</strong>).
              </div>
              <input
                type="email"
                value={billingEmailVal}
                onChange={e => { setBillingEmailVal(e.target.value); setBillingEmailMsg(''); }}
                placeholder={company?.email + ' (account email — fallback)'}
                style={{ width: '100%', boxSizing: 'border-box', padding: '0.38rem 0.55rem', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.8rem', marginBottom: '0.45rem' }}
              />
              {billingEmailMsg && (
                <div style={{ fontSize: '0.75rem', marginBottom: '0.4rem', color: billingEmailMsg === 'success' ? '#28a745' : '#dc3545', fontWeight: 700 }}>
                  {billingEmailMsg === 'success' ? '✓ Billing email saved.' : billingEmailMsg}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <button
                  disabled={billingEmailLoading}
                  onClick={async () => {
                    setBillingEmailLoading(true);
                    setBillingEmailMsg('');
                    try {
                      await axios.patch('/api/companies/me/billing-email', { billingEmail: billingEmailVal }, { headers: { Authorization: `Bearer ${token}` } });
                      setCompany(prev => ({ ...prev, billingEmail: billingEmailVal || null }));
                      setBillingEmailMsg('success');
                    } catch (err) {
                      setBillingEmailMsg(err.response?.data?.error || 'Save failed.');
                    } finally {
                      setBillingEmailLoading(false);
                    }
                  }}
                  style={{ flex: 1, padding: '0.38rem', fontSize: '0.79rem', backgroundColor: billingEmailLoading ? '#aaa' : '#0d3165', color: '#fff', border: 'none', borderRadius: '5px', fontWeight: 700, cursor: billingEmailLoading ? 'not-allowed' : 'pointer' }}
                >
                  {billingEmailLoading ? 'Saving…' : 'Save Billing Email'}
                </button>
                {billingEmailVal && (
                  <button
                    disabled={billingEmailLoading}
                    onClick={async () => {
                      setBillingEmailLoading(true);
                      setBillingEmailMsg('');
                      try {
                        await axios.patch('/api/companies/me/billing-email', { billingEmail: '' }, { headers: { Authorization: `Bearer ${token}` } });
                        setCompany(prev => ({ ...prev, billingEmail: null }));
                        setBillingEmailVal('');
                        setBillingEmailMsg('success');
                      } catch (err) {
                        setBillingEmailMsg(err.response?.data?.error || 'Clear failed.');
                      } finally {
                        setBillingEmailLoading(false);
                      }
                    }}
                    style={{ padding: '0.38rem 0.7rem', fontSize: '0.79rem', backgroundColor: '#fff', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '5px', cursor: billingEmailLoading ? 'not-allowed' : 'pointer' }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="recommended-list">
            <div className="recommended-item" onClick={() => { setShowExtForm(true); setShowForm(false); setSubmittedArticle(null); }} style={{ cursor: 'pointer' }}>
              <span className="recommended-icon">🔗</span>
              <span className="recommended-text">Create External Link &amp; Article Card</span>
            </div>
            <div className="recommended-item" onClick={handleCreateArticle} style={{ cursor: 'pointer', marginTop: '0.4rem' }}>
              <span className="recommended-icon">📝</span>
              <span className="recommended-text">Create New Article</span>
            </div>
            <div
              className="recommended-item"
              onClick={() => window.open(`/${encodeURIComponent(company?.company_name || '')}`, '_blank')}
              style={{ cursor: 'pointer', marginTop: '0.4rem' }}
            >
              <span className="recommended-icon">🏠</span>
              <span className="recommended-text">View Your Silo</span>
            </div>
          </div>

          {/* Authors panel */}
          <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#888', paddingBottom: '8px', borderBottom: `2px solid ${T.accent}`, marginBottom: '10px', marginTop: '1.5rem', fontFamily: 'Arial, sans-serif' }}>Authors</div>
          {authorMsg && (
            <div style={{ fontSize: '0.78rem', marginBottom: '0.5rem', color: authorMsg.includes('removed') || authorMsg.includes('success') ? '#28a745' : '#dc3545' }}>
              {authorMsg}
            </div>
          )}
          {authors.length === 0 && !showAuthorForm && (
            <div style={{ fontSize: '0.82rem', color: '#aaa', marginBottom: '0.75rem' }}>No authors yet.</div>
          )}
          {authors.map(a => (
            <div key={a._id} style={{ marginBottom: '0.65rem', paddingBottom: '0.65rem', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.4rem' }}>
                <div>
                  <div style={{ fontWeight: '600', color: '#333', fontSize: '0.82rem' }}>
                    {a.display_name}{a.kudos > 0 ? <span style={{ color: '#7b2ff7', fontWeight: '400', fontSize: '0.76rem' }}> ({a.kudos} ⭐)</span> : ''}
                  </div>
                  <div style={{ color: '#888', fontSize: '0.76rem' }}>@{a.username}</div>
                  {a.department && (
                    <div style={{ fontSize: '0.72rem', color: '#3366cc', marginTop: '0.1rem' }}>📂 {a.department}</div>
                  )}
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginTop: '0.35rem', cursor: 'pointer', fontSize: '0.74rem', color: a.trusted ? '#28a745' : '#888', userSelect: 'none' }}
                    title={a.trusted ? 'Articles publish immediately when submitted' : 'Articles go to pending review when submitted'}
                  >
                    <input
                      type="checkbox"
                      checked={!!a.trusted}
                      onChange={() => handleToggleTrusted(a._id, a.trusted)}
                      style={{ width: 'auto', margin: 0, cursor: 'pointer' }}
                    />
                    Publish without review
                  </label>
                </div>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <button
                    onClick={() => setAuthorGearOpen(authorGearOpen === a._id ? null : a._id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#888', padding: '0.1rem 0.25rem', lineHeight: 1 }}
                    title="Author options"
                  >
                    ⚙️
                  </button>
                  {authorGearOpen === a._id && (
                    <div style={{ position: 'absolute', right: 0, top: '100%', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '7px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, minWidth: '130px', overflow: 'hidden' }}>
                      <button
                        onClick={() => { setEditAuthorTarget(a); setEditAuthorForm({ display_name: a.display_name, department: a.department || '' }); setEditAuthorMsg(''); setAuthorGearOpen(null); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.85rem', fontSize: '0.82rem', background: 'none', border: 'none', cursor: 'pointer', color: '#333' }}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => { setRemoveTarget(a); setRemovePassword(''); setRemoveError(''); setAuthorMsg(''); setAuthorGearOpen(null); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.85rem', fontSize: '0.82rem', background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545' }}
                      >
                        🗑️ Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Editors panel */}
          <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: '#888', paddingBottom: '8px', borderBottom: `2px solid ${T.accent}`, marginBottom: '10px', marginTop: '1.75rem', fontFamily: 'Arial, sans-serif' }}>Editors</div>
          {editorMsg && (
            <div style={{ fontSize: '0.78rem', marginBottom: '0.5rem', color: editorMsg.includes('removed') || editorMsg.includes('success') ? '#28a745' : '#dc3545' }}>
              {editorMsg}
            </div>
          )}
          {editors.length === 0 && !showEditorForm && (
            <div style={{ fontSize: '0.82rem', color: '#aaa', marginBottom: '0.75rem' }}>No editors yet.</div>
          )}
          {editors.map(ed => (
            <div key={ed._id} style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.4rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', color: '#333', fontSize: '0.82rem' }}>{ed.display_name}</div>
                  <div style={{ color: '#888', fontSize: '0.76rem' }}>@{ed.username}</div>
                  {ed.department && (
                    <div style={{ fontSize: '0.72rem', color: '#3366cc', marginTop: '0.1rem' }}>📂 {ed.department}</div>
                  )}
                  {ed.authors?.length > 0 ? (
                    <div style={{ fontSize: '0.72rem', color: '#555', marginTop: '0.2rem' }}>
                      👥 {ed.authors.map(a => a.display_name).join(', ')}
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.72rem', color: '#bbb', marginTop: '0.2rem' }}>No authors assigned</div>
                  )}
                </div>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <button
                    onClick={() => setEditorGearOpen(editorGearOpen === ed._id ? null : ed._id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#888', padding: '0.1rem 0.25rem', lineHeight: 1 }}
                    title="Editor options"
                  >
                    ⚙️
                  </button>
                  {editorGearOpen === ed._id && (
                    <div style={{ position: 'absolute', right: 0, top: '100%', backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '7px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, minWidth: '155px', overflow: 'hidden' }}>
                      <button
                        onClick={() => { setEditEditorTarget(ed); setEditEditorForm({ display_name: ed.display_name, department: ed.department || '' }); setEditEditorMsg(''); setEditorGearOpen(null); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.85rem', fontSize: '0.82rem', background: 'none', border: 'none', cursor: 'pointer', color: '#333' }}
                      >
                        ✏️ Edit Name / Dept
                      </button>
                      <button
                        onClick={() => { setAssignTarget(ed); setAssignSelected((ed.authors || []).map(a => a._id.toString())); setAssignMsg(''); setEditorGearOpen(null); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.85rem', fontSize: '0.82rem', background: 'none', border: 'none', cursor: 'pointer', color: '#3366cc' }}
                      >
                        👥 Assign Authors
                      </button>
                      <button
                        onClick={() => { setRemoveEditorTarget(ed); setRemoveEditorPassword(''); setRemoveEditorError(''); setEditorMsg(''); setEditorGearOpen(null); }}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.85rem', fontSize: '0.82rem', background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545' }}
                      >
                        🗑️ Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {showEditorForm ? (
            <form onSubmit={handleAddEditor} style={{ marginTop: '0.5rem' }}>
              {[
                { name: 'display_name', placeholder: 'Display Name' },
                { name: 'username',     placeholder: 'Username' },
                { name: 'email',        placeholder: 'Email', type: 'email' },
                { name: 'password',     placeholder: 'Password', type: 'password' },
              ].map(f => (
                <input
                  key={f.name}
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={editorForm[f.name]}
                  onChange={e => setEditorForm(prev => ({ ...prev, [f.name]: e.target.value }))}
                  required
                  style={{ display: 'block', width: '100%', marginBottom: '0.4rem', padding: '0.35rem 0.6rem', fontSize: '0.82rem', borderRadius: '5px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
              ))}
              <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.25rem' }}>
                <button type="submit" style={{ flex: 1, padding: '0.35rem', fontSize: '0.8rem', backgroundColor: '#343a40', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                  Add
                </button>
                <button type="button" onClick={() => { setShowEditorForm(false); setEditorMsg(''); }} style={{ flex: 1, padding: '0.35rem', fontSize: '0.8rem', backgroundColor: '#fff', color: '#666', border: '1px solid #ccc', borderRadius: '5px', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => { setShowEditorForm(true); setEditorMsg(''); }}
              style={{ marginTop: '0.5rem', width: '100%', padding: '0.4rem', fontSize: '0.82rem', backgroundColor: '#f0f4ff', color: '#3366cc', border: '1px solid #c0cff8', borderRadius: '6px', cursor: 'pointer' }}
            >
              + Add Editor
            </button>
          )}

          {showAuthorForm ? (
            <form onSubmit={handleAddAuthor} style={{ marginTop: '0.5rem' }}>
              {[
                { name: 'display_name', placeholder: 'Display Name' },
                { name: 'username',     placeholder: 'Username' },
                { name: 'email',        placeholder: 'Email', type: 'email' },
                { name: 'password',     placeholder: 'Password', type: 'password' },
              ].map(f => (
                <input
                  key={f.name}
                  type={f.type || 'text'}
                  placeholder={f.placeholder}
                  value={authorForm[f.name]}
                  onChange={e => setAuthorForm(prev => ({ ...prev, [f.name]: e.target.value }))}
                  required
                  style={{ display: 'block', width: '100%', marginBottom: '0.4rem', padding: '0.35rem 0.6rem', fontSize: '0.82rem', borderRadius: '5px', border: '1px solid #ccc', boxSizing: 'border-box' }}
                />
              ))}
              <select
                value={authorForm.department}
                onChange={e => setAuthorForm(prev => ({ ...prev, department: e.target.value }))}
                style={{ display: 'block', width: '100%', marginBottom: '0.4rem', padding: '0.35rem 0.6rem', fontSize: '0.82rem', borderRadius: '5px', border: '1px solid #ccc', boxSizing: 'border-box', color: authorForm.department ? '#333' : '#888' }}
              >
                <option value="">Department (optional)</option>
                {DEPT_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.25rem' }}>
                <button type="submit" style={{ flex: 1, padding: '0.35rem', fontSize: '0.8rem', backgroundColor: '#343a40', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                  Add
                </button>
                <button type="button" onClick={() => { setShowAuthorForm(false); setAuthorMsg(''); }} style={{ flex: 1, padding: '0.35rem', fontSize: '0.8rem', backgroundColor: '#fff', color: '#666', border: '1px solid #ccc', borderRadius: '5px', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => { setShowAuthorForm(true); setAuthorMsg(''); }}
              style={{ marginTop: '0.5rem', width: '100%', padding: '0.4rem', fontSize: '0.82rem', backgroundColor: '#f0f4ff', color: '#3366cc', border: '1px solid #c0cff8', borderRadius: '6px', cursor: 'pointer' }}
            >
              + Add Author
            </button>
          )}

        {/* ── Embed Integration (shown via settings gear) ───── */}
        {settingsView === 'embed' && <>

        {/* BASE: upgrade prompt */}
        {company?.subscription_tier === 'base' && (
          <div style={{ fontSize: '0.8rem', color: '#555', lineHeight: 1.5 }}>
            <p style={{ marginBottom: '0.5rem' }}>Embed PubSilo purchase buttons on external websites.</p>
            <p style={{ marginBottom: '0.35rem' }}><strong>Elevated</strong> ($299/mo) — button generator for your PubSilo articles; readers return to their site after purchase.</p>
            <p style={{ marginBottom: '0.75rem' }}><strong>Grand</strong> ($749/mo) — pure plug-and-play; one key works for unlimited articles, no per-article setup.</p>
            <p style={{ fontSize: '0.75rem', color: '#999' }}>Contact your PubSilo admin to upgrade.</p>
          </div>
        )}

        {/* ELEVATED: button generator (uses PubSilo article _id) */}
        {company?.subscription_tier === 'elevated' && (
          <div style={{ fontSize: '0.82rem' }}>
            <p style={{ color: '#555', marginBottom: '0.75rem' }}>Generate a purchase button for any of your approved articles. After purchase, readers are sent back to your external page.</p>
            <label style={{ fontWeight: '600', display: 'block', marginBottom: '0.2rem' }}>Article</label>
            <select
              value={elGenArticle}
              onChange={e => { setElGenArticle(e.target.value); setElGenSnippet(''); }}
              style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.8rem', marginBottom: '0.5rem', boxSizing: 'border-box' }}
            >
              <option value="">— Select article —</option>
              {articles.filter(a => a.status === 'approved').map(a => (
                <option key={a._id} value={a._id}>{a.title}</option>
              ))}
            </select>
            <label style={{ fontWeight: '600', display: 'block', marginBottom: '0.2rem' }}>Return URL (your external page)</label>
            <input
              type="url"
              value={elGenReturn}
              onChange={e => { setElGenReturn(e.target.value); setElGenSnippet(''); }}
              placeholder="https://yoursite.com/article"
              style={{ width: '100%', padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.8rem', marginBottom: '0.5rem', boxSizing: 'border-box' }}
            />
            <button
              onClick={handleElGenerate}
              disabled={!elGenArticle || !elGenReturn}
              style={{ width: '100%', padding: '0.4rem', fontSize: '0.8rem', backgroundColor: '#1a6fc7', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', marginBottom: '0.5rem', opacity: (!elGenArticle || !elGenReturn) ? 0.5 : 1 }}
            >
              Generate Button Snippet
            </button>
            {elGenSnippet && (
              <>
                <pre style={{ fontSize: '0.72rem', background: '#f4f8fd', border: '1px solid #d0e4f5', borderRadius: '5px', padding: '0.5rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: '0.35rem' }}>
                  {elGenSnippet}
                </pre>
                <button
                  onClick={() => { navigator.clipboard.writeText(elGenSnippet); }}
                  style={{ width: '100%', padding: '0.35rem', fontSize: '0.78rem', backgroundColor: '#e8f4fd', color: '#1a6fc7', border: '1px solid #d0e4f5', borderRadius: '5px', cursor: 'pointer' }}
                >
                  Copy Snippet
                </button>
                <p style={{ fontSize: '0.72rem', color: '#888', marginTop: '0.35rem' }}>
                  Verify token server-side: <code>GET /api/embed/verify?article=ID&amp;token=TOKEN</code>
                </p>
              </>
            )}
          </div>
        )}

        {/* GRAND: keys + button generator + embed article list */}
        {company?.subscription_tier === 'grand' && (
          <div style={{ fontSize: '0.82rem' }}>
            <button
              onClick={() => setEmbedInstructionsOpen(true)}
              style={{ width: '100%', marginBottom: '0.85rem', padding: '0.45rem', fontSize: '0.8rem', backgroundColor: '#e8f4fd', color: '#0d3165', border: '1px solid #b0cfe8', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
            >
              📋 Integration Instructions
            </button>
            {/* Keys section */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontWeight: '600', marginBottom: '0.4rem' }}>Embed Keys</div>
              {embedKeysMsg && (
                <p style={{ fontSize: '0.78rem', color: embedKeysMsg.includes('Failed') ? '#dc3545' : '#28a745', marginBottom: '0.4rem' }}>{embedKeysMsg}</p>
              )}
              {company.embedKey ? (
                <>
                  <div style={{ marginBottom: '0.4rem' }}>
                    <span style={{ color: '#555' }}>Public Key: </span>
                    <code style={{ fontSize: '0.72rem', wordBreak: 'break-all' }}>{embedKeyOnce || company.embedKey}</code>
                    <button
                      onClick={() => navigator.clipboard.writeText(embedKeyOnce || company.embedKey)}
                      style={{ marginLeft: '0.4rem', fontSize: '0.7rem', padding: '0.1rem 0.4rem', border: '1px solid #ccc', borderRadius: '3px', cursor: 'pointer', background: '#f4f4f4' }}
                    >Copy</button>
                  </div>
                  {embedSecretOnce ? (
                    <div style={{ background: '#fff8ee', border: '1px solid #f5d87a', borderRadius: '5px', padding: '0.5rem 0.65rem', marginBottom: '0.4rem' }}>
                      <div style={{ fontWeight: '600', marginBottom: '0.2rem', color: '#b06000' }}>Secret (save now — shown once):</div>
                      <code style={{ fontSize: '0.72rem', wordBreak: 'break-all' }}>{embedSecretOnce}</code>
                      <button
                        onClick={() => navigator.clipboard.writeText(embedSecretOnce)}
                        style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.7rem', padding: '0.2rem 0.5rem', border: '1px solid #f5d87a', borderRadius: '3px', cursor: 'pointer', background: '#fff' }}
                      >Copy Secret</button>
                    </div>
                  ) : (
                    <p style={{ color: '#888', fontSize: '0.76rem', marginBottom: '0.4rem' }}>Secret: <em>Contact PubSilo support to reset your keys.</em></p>
                  )}
                  <p style={{ fontSize: '0.72rem', color: '#dc3545', marginBottom: '0.25rem' }}>⚠ Keep your secret server-side only — never expose it in client JavaScript.</p>
                </>
              ) : (
                <button
                  onClick={() => handleGenerateKeys(false)}
                  disabled={embedKeysLoading}
                  style={{ width: '100%', padding: '0.4rem', fontSize: '0.82rem', backgroundColor: '#0d3165', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                >
                  {embedKeysLoading ? 'Generating…' : 'Generate Embed Keys'}
                </button>
              )}
            </div>

            {/* Grand button generator */}
            {company.embedKey && (
              <div style={{ marginBottom: '1rem', borderTop: '1px solid #e8e8e8', paddingTop: '0.75rem' }}>
                <div style={{ fontWeight: '600', marginBottom: '0.4rem' }}>Button Generator</div>
                <input type="text" placeholder="Article Title" value={grGenTitle} onChange={e => { setGrGenTitle(e.target.value); setGrGenSnippet(''); }} style={inputStyle} />
                <input type="text" placeholder="Your Article Slug (e.g. my-article-2026)" value={grGenSlug} onChange={e => { setGrGenSlug(e.target.value); setGrGenSnippet(''); }} style={inputStyle} />
                <input type="number" placeholder="Price (Article Credits)" min="4" max="150" value={grGenPrice} onChange={e => { setGrGenPrice(e.target.value); setGrGenSnippet(''); }} style={inputStyle} />
                <input type="url" placeholder="Return URL (your external page)" value={grGenReturn} onChange={e => { setGrGenReturn(e.target.value); setGrGenSnippet(''); }} style={inputStyle} />
                <select value={grGenAudience} onChange={e => { setGrGenAudience(e.target.value); setGrGenSnippet(''); }} style={{ ...inputStyle, marginBottom: '0.5rem' }}>
                  <option value="local">Local</option>
                  <option value="state">State</option>
                  <option value="regional">Regional (multi-state)</option>
                  <option value="national">National</option>
                  <option value="world">World</option>
                </select>
                <button
                  onClick={handleGrGenerate}
                  disabled={!grGenSlug || !grGenReturn}
                  style={{ width: '100%', padding: '0.4rem', fontSize: '0.8rem', backgroundColor: '#0d3165', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', marginBottom: '0.5rem', opacity: (!grGenSlug || !grGenReturn) ? 0.5 : 1 }}
                >
                  Generate Button Snippet
                </button>
                {grGenMsg && <p style={{ fontSize: '0.72rem', color: grGenMsg.startsWith('✓') ? '#28a745' : '#cc6600', marginBottom: '0.3rem' }}>{grGenMsg}</p>}
                <p style={{ fontSize: '0.72rem', color: '#888', marginBottom: '0.15rem' }}>Generating the snippet registers the article and locks the price immediately — readers cannot change it via the URL. Update price or mode later in the Embed Articles list below.</p>
                <p style={{ fontSize: '0.72rem', color: '#888', marginBottom: '0.15rem' }}>The verify script calls <code>unlockContent(art)</code> — that is a placeholder. Replace it with whatever function in your site shows the gated content (e.g. <code>unlockArticle()</code> if you have a custom paywall.js). For sites with an existing paywall script, integrate the token check into that file instead of pasting the inline verify script.</p>
                <p style={{ fontSize: '0.72rem', color: '#888', marginBottom: '0.3rem' }}><strong>CMS sites:</strong> save the slug you enter here to your article's database record, then render it as <code>{'<meta name="pubsilo-article" content="YOUR_SLUG">'}</code> on the article page. Your paywall script reads it from the DOM — no hardcoded slug map needed.</p>
                {grGenSnippet && (
                  <>
                    <pre style={{ fontSize: '0.72rem', background: '#f4f8fd', border: '1px solid #d0e4f5', borderRadius: '5px', padding: '0.5rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', marginBottom: '0.35rem' }}>
                      {grGenSnippet}
                    </pre>
                    <button
                      onClick={() => navigator.clipboard.writeText(grGenSnippet)}
                      style={{ width: '100%', padding: '0.35rem', fontSize: '0.78rem', backgroundColor: '#e8f4fd', color: '#1a6fc7', border: '1px solid #d0e4f5', borderRadius: '5px', cursor: 'pointer' }}
                    >
                      Copy Snippet
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Grand embed article list */}
            {embedArticles.length > 0 && (
              <div style={{ borderTop: '1px solid #e8e8e8', paddingTop: '0.75rem' }}>
                <div style={{ fontWeight: '600', marginBottom: '0.4rem' }}>Embed Articles</div>
                {embedPriceMsg && <p style={{ fontSize: '0.76rem', color: '#28a745', marginBottom: '0.3rem' }}>{embedPriceMsg}</p>}
                {embedArticles.map(a => {
                  const currentMode = embedModeEdit[a._id] !== undefined ? embedModeEdit[a._id] : (a.pricingMode || 'manual');
                  const autoVals = embedAutoEdit[a._id] || a.autoAdjust || {};
                  const isDirty = embedPriceEdit[a._id] !== undefined || embedModeEdit[a._id] !== undefined || embedAutoEdit[a._id] !== undefined;
                  return (
                  <div key={a._id} style={{ marginBottom: '0.85rem', paddingBottom: '0.85rem', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ fontWeight: '600', color: '#333', fontSize: '0.78rem', marginBottom: '0.1rem' }}>{a.title}</div>
                    <div style={{ color: '#888', fontSize: '0.72rem', marginBottom: '0.4rem' }}>slug: <code>{a.external_id}</code> · {a.purchased} purchase{a.purchased !== 1 ? 's' : ''}</div>

                    {/* Price row */}
                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', marginBottom: '0.35rem' }}>
                      <span style={{ fontSize: '0.72rem', color: '#555', minWidth: '36px' }}>Price:</span>
                      <input
                        type="number"
                        min="4"
                        max="150"
                        value={embedPriceEdit[a._id] !== undefined ? embedPriceEdit[a._id] : a.token_cost}
                        onChange={e => setEmbedPriceEdit(prev => ({ ...prev, [a._id]: e.target.value }))}
                        style={{ width: '60px', padding: '0.25rem 0.4rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.78rem' }}
                      />
                      <span style={{ fontSize: '0.72rem', color: '#888' }}>cr</span>
                    </div>

                    {/* Pricing mode toggle */}
                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', marginBottom: currentMode === 'auto' ? '0.35rem' : 0 }}>
                      <span style={{ fontSize: '0.72rem', color: '#555', minWidth: '36px' }}>Mode:</span>
                      <button
                        onClick={() => setEmbedModeEdit(prev => ({ ...prev, [a._id]: 'manual' }))}
                        style={{ padding: '0.2rem 0.55rem', fontSize: '0.72rem', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', backgroundColor: currentMode === 'manual' ? '#0d3165' : '#f4f4f4', color: currentMode === 'manual' ? '#fff' : '#555' }}
                      >Manual</button>
                      <button
                        onClick={() => setEmbedModeEdit(prev => ({ ...prev, [a._id]: 'auto' }))}
                        style={{ padding: '0.2rem 0.55rem', fontSize: '0.72rem', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', backgroundColor: currentMode === 'auto' ? '#0d3165' : '#f4f4f4', color: currentMode === 'auto' ? '#fff' : '#555' }}
                      >Auto-decrease</button>
                    </div>

                    {/* Auto-decrease config (shown only when mode = auto) */}
                    {currentMode === 'auto' && (
                      <div style={{ background: '#f8f9ff', border: '1px solid #dde3f5', borderRadius: '6px', padding: '0.6rem 0.75rem', marginTop: '0.35rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 0.75rem' }}>
                        <label style={{ fontSize: '0.7rem', color: '#555' }}>
                          First decrease date
                          <input
                            type="date"
                            value={autoVals.firstDecreaseDate ? new Date(autoVals.firstDecreaseDate).toISOString().split('T')[0] : ''}
                            onChange={e => setEmbedAutoEdit(prev => ({ ...prev, [a._id]: { ...(prev[a._id] || a.autoAdjust || {}), firstDecreaseDate: e.target.value } }))}
                            style={{ display: 'block', width: '100%', marginTop: '0.2rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.72rem' }}
                          />
                        </label>
                        <label style={{ fontSize: '0.7rem', color: '#555' }}>
                          Decrease % per step
                          <input
                            type="number"
                            min="1" max="99"
                            placeholder="e.g. 10"
                            value={autoVals.decreasePercent ?? ''}
                            onChange={e => setEmbedAutoEdit(prev => ({ ...prev, [a._id]: { ...(prev[a._id] || a.autoAdjust || {}), decreasePercent: e.target.value } }))}
                            style={{ display: 'block', width: '100%', marginTop: '0.2rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.72rem' }}
                          />
                        </label>
                        <label style={{ fontSize: '0.7rem', color: '#555' }}>
                          Every N days
                          <input
                            type="number"
                            min="1"
                            placeholder="e.g. 7"
                            value={autoVals.frequencyDays ?? ''}
                            onChange={e => setEmbedAutoEdit(prev => ({ ...prev, [a._id]: { ...(prev[a._id] || a.autoAdjust || {}), frequencyDays: e.target.value } }))}
                            style={{ display: 'block', width: '100%', marginTop: '0.2rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.72rem' }}
                          />
                        </label>
                        <label style={{ fontSize: '0.7rem', color: '#555' }}>
                          Skip if daily avg above (optional)
                          <input
                            type="number"
                            min="0"
                            placeholder="e.g. 5"
                            value={autoVals.skipIfDailyAbove ?? ''}
                            onChange={e => setEmbedAutoEdit(prev => ({ ...prev, [a._id]: { ...(prev[a._id] || a.autoAdjust || {}), skipIfDailyAbove: e.target.value === '' ? null : e.target.value } }))}
                            style={{ display: 'block', width: '100%', marginTop: '0.2rem', padding: '0.2rem 0.35rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.72rem' }}
                          />
                        </label>
                      </div>
                    )}

                    {isDirty && (
                      <button
                        onClick={() => handleEmbedPriceUpdate(a._id)}
                        style={{ marginTop: '0.4rem', padding: '0.25rem 0.65rem', fontSize: '0.72rem', backgroundColor: '#1a6fc7', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                      >Save</button>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        </>}
        </div>
      </div>

      {/* Embed Integration Instructions modal */}
      {embedInstructionsOpen && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '1rem' }}
          onClick={() => setEmbedInstructionsOpen(false)}
        >
          <div
            style={{ backgroundColor: '#fff', borderRadius: '14px', padding: '2rem', maxWidth: '640px', width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.22)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
              <h3 style={{ margin: 0, color: '#0d3165', fontSize: '1.1rem' }}>Embedding PubSilo on Your Website</h3>
              <button onClick={() => setEmbedInstructionsOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#888', lineHeight: 1 }}>×</button>
            </div>

            <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: '1.25rem', lineHeight: 1.6 }}>
              Add PubSilo purchase buttons to any article on your external website in 2 steps. No CSS, no backend changes — just copy and paste.
            </p>

            {/* Step 1 */}
            <div style={instrStep}>
              <div style={instrNum}>1</div>
              <div style={{ flex: 1 }}>
                <div style={instrTitle}>Generate a button snippet from this dashboard</div>
                <p style={instrBody}>
                  In the <strong>Button Generator</strong> below, enter your article slug (your article's unique ID on your site), title, token price, and the full URL of the page readers should return to after purchase. Click <em>Generate Button Snippet</em> and copy the result.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div style={instrStep}>
              <div style={instrNum}>2</div>
              <div style={{ flex: 1 }}>
                <div style={instrTitle}>Paste the snippet into your article page HTML</div>
                <p style={instrBody}>
                  The snippet contains two parts — paste both into your article page:
                </p>
                <ul style={{ fontSize: '0.83rem', color: '#444', paddingLeft: '1.2rem', lineHeight: 1.7, marginBottom: '0.75rem' }}>
                  <li><strong>The button</strong> — an <code>&lt;a&gt;</code> tag with the PubSilo logo. Place it wherever you want the paywall lock to appear.</li>
                  <li><strong>The verification script</strong> — a small inline <code>&lt;script&gt;</code> block. Paste it once, anywhere before <code>&lt;/body&gt;</code>.</li>
                </ul>
                <p style={instrBody}>
                  When a reader returns from PubSilo after purchasing, the script checks the URL for <code>?article=…&amp;token=…</code>, verifies the token with PubSilo, and calls your <code>unlockContent(articleSlug)</code> function.
                </p>
              </div>
            </div>

            {/* unlockContent note */}
            <div style={{ background: '#f4f8fd', border: '1px solid #d0e4f5', borderRadius: '8px', padding: '1rem 1.1rem', marginBottom: '1.25rem' }}>
              <div style={{ fontWeight: '700', fontSize: '0.83rem', color: '#0d3165', marginBottom: '0.4rem' }}>Your unlockContent function</div>
              <p style={{ fontSize: '0.82rem', color: '#444', marginBottom: '0.5rem', lineHeight: 1.6 }}>
                You write this function — it's the part that shows your full article content. The slug passed in matches the article slug you entered in the button generator.
              </p>
              <pre style={{ fontSize: '0.78rem', background: '#e8f4fd', borderRadius: '5px', padding: '0.65rem 0.85rem', overflowX: 'auto', margin: 0 }}>{`function unlockContent(slug) {
  // show your full article — however your site works
  document.getElementById('paywall-' + slug).style.display = 'none';
  document.getElementById('full-' + slug).style.display = 'block';
}`}</pre>
            </div>

            {/* Token persistence note */}
            <div style={{ background: '#fff8ee', border: '1px solid #f5d87a', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1.25rem' }}>
              <div style={{ fontWeight: '700', fontSize: '0.83rem', color: '#b06000', marginBottom: '0.3rem' }}>Tip: remember unlocked articles across page loads</div>
              <p style={{ fontSize: '0.82rem', color: '#555', margin: 0, lineHeight: 1.6 }}>
                After verifying, save the unlocked slug to <code>localStorage</code> so the article stays unlocked when the reader revisits:
              </p>
              <pre style={{ fontSize: '0.78rem', background: '#fff8ee', borderRadius: '5px', padding: '0.5rem 0.75rem', overflowX: 'auto', margin: '0.5rem 0 0' }}>{`// In your unlockContent function:
localStorage.setItem('unlocked_' + slug, '1');

// On every page load, also check localStorage:
if (localStorage.getItem('unlocked_' + slug)) unlockContent(slug);`}</pre>
            </div>

            {/* Price note */}
            <div style={{ fontSize: '0.82rem', color: '#666', lineHeight: 1.6 }}>
              <strong>Changing the price:</strong> The Article Credit price in the button URL is only used the first time a reader accesses that article. After that it's frozen in PubSilo's system. To change it, use the <em>Embed Articles</em> list in this dashboard to update the price directly.
            </div>

            <button
              onClick={() => setEmbedInstructionsOpen(false)}
              style={{ marginTop: '1.5rem', width: '100%', padding: '0.65rem', backgroundColor: '#0d3165', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer' }}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Purchase history graph modal */}
      {graphArticle && (
        <PurchaseGraph
          articleId={graphArticle._id}
          articleTitle={graphArticle.title}
          onClose={() => setGraphArticle(null)}
        />
      )}

      {/* Draft preview modal */}
      {showDraftPreview && (
        <DraftPreview
          title={formData.title}
          content={buildPreviewHTML(formData.content)}
          writerName={formData.writer_name}
          genreTree={genreTree}
          tokenCost={formData.token_cost}
          onClose={() => setShowDraftPreview(false)}
        />
      )}

      {/* Article preview modal */}
      {previewArticle && (
        <ArticlePreview article={previewArticle} onClose={() => setPreviewArticle(null)} />
      )}

      {/* Edit editor modal */}
      {editEditorTarget && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setEditEditorTarget(null)}
        >
          <div
            style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '400px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}
          >
            <h4 style={{ marginBottom: '1.25rem' }}>Edit Editor</h4>
            <form onSubmit={handleEditEditorSubmit}>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.82rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>Display Name</label>
                <input
                  type="text"
                  value={editEditorForm.display_name}
                  onChange={e => setEditEditorForm(f => ({ ...f, display_name: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.88rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ fontSize: '0.82rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>Department</label>
                <select
                  value={editEditorForm.department}
                  onChange={e => setEditEditorForm(f => ({ ...f, department: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.88rem', boxSizing: 'border-box' }}
                >
                  <option value="">— None —</option>
                  {DEPT_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              {editEditorMsg && <div style={{ fontSize: '0.82rem', color: '#dc3545', marginBottom: '0.75rem' }}>{editEditorMsg}</div>}
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <button type="submit" style={{ flex: 1, padding: '0.65rem', backgroundColor: '#343a40', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer' }}>
                  Save Changes
                </button>
                <button type="button" onClick={() => setEditEditorTarget(null)} style={{ flex: 1, padding: '0.65rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.9rem', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove editor confirmation modal */}
      {removeEditorTarget && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '400px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h4 style={{ marginBottom: '0.5rem', color: '#1a1a1a' }}>Remove Editor</h4>
            <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.25rem' }}>
              You are about to remove <strong>{removeEditorTarget.display_name}</strong> (@{removeEditorTarget.username}).
            </p>
            <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1.25rem' }}>
              Their login access will be revoked. Author assignments will be lost.
            </p>
            <label style={{ fontSize: '0.82rem', fontWeight: '600', color: '#333', display: 'block', marginBottom: '0.35rem' }}>
              Enter your company password to confirm
            </label>
            <input
              type="password"
              value={removeEditorPassword}
              onChange={e => { setRemoveEditorPassword(e.target.value); setRemoveEditorError(''); }}
              placeholder="Company password"
              autoFocus
              style={{ width: '100%', padding: '0.55rem 0.75rem', fontSize: '0.9rem', border: `1px solid ${removeEditorError ? '#dc3545' : '#ccc'}`, borderRadius: '6px', marginBottom: '0.4rem', boxSizing: 'border-box' }}
            />
            {removeEditorError && <div style={{ color: '#dc3545', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{removeEditorError}</div>}
            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.75rem' }}>
              <button
                onClick={handleRemoveEditorConfirm}
                disabled={removeEditorLoading || !removeEditorPassword}
                style={{ flex: 1, padding: '0.65rem', backgroundColor: removeEditorLoading || !removeEditorPassword ? '#aaa' : '#dc3545', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '600', fontSize: '0.9rem', cursor: removeEditorLoading || !removeEditorPassword ? 'not-allowed' : 'pointer' }}
              >
                {removeEditorLoading ? 'Removing...' : 'Remove Editor'}
              </button>
              <button
                onClick={() => { setRemoveEditorTarget(null); setRemoveEditorPassword(''); setRemoveEditorError(''); }}
                style={{ flex: 1, padding: '0.65rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign authors to editor modal */}
      {assignTarget && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h4 style={{ marginBottom: '0.25rem' }}>Assign Authors</h4>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1.25rem' }}>
              Select which authors <strong>{assignTarget.display_name}</strong> is responsible for reviewing.
            </p>
            {authors.length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: '#aaa' }}>No authors exist yet. Add authors first.</p>
            ) : (
              <div style={{ maxHeight: '240px', overflowY: 'auto', marginBottom: '1rem', border: '1px solid #e0e0e0', borderRadius: '7px' }}>
                {authors.map(a => {
                  const checked = assignSelected.includes(a._id.toString());
                  return (
                    <label
                      key={a._id}
                      style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.55rem 0.85rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', backgroundColor: checked ? '#f0f4ff' : '#fff' }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setAssignSelected(prev =>
                          checked ? prev.filter(id => id !== a._id.toString()) : [...prev, a._id.toString()]
                        )}
                      />
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#333' }}>{a.display_name}</div>
                        <div style={{ fontSize: '0.76rem', color: '#888' }}>@{a.username}{a.department ? ` · ${a.department}` : ''}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            {assignMsg && <div style={{ fontSize: '0.8rem', color: '#28a745', marginBottom: '0.75rem' }}>{assignMsg}</div>}
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button
                onClick={handleAssignAuthors}
                style={{ flex: 1, padding: '0.65rem', backgroundColor: '#343a40', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                Save
              </button>
              <button
                onClick={() => { setAssignTarget(null); setAssignSelected([]); setAssignMsg(''); }}
                style={{ flex: 1, padding: '0.65rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit author modal */}
      {editAuthorTarget && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setEditAuthorTarget(null)}
        >
          <div
            style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '400px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}
          >
            <h4 style={{ marginBottom: '1.25rem' }}>Edit Author</h4>
            <form onSubmit={handleEditAuthorSubmit}>
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ fontSize: '0.82rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>Display Name</label>
                <input
                  type="text"
                  value={editAuthorForm.display_name}
                  onChange={e => setEditAuthorForm(f => ({ ...f, display_name: e.target.value }))}
                  required
                  style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.88rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ fontSize: '0.82rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>Department</label>
                <select
                  value={editAuthorForm.department}
                  onChange={e => setEditAuthorForm(f => ({ ...f, department: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #ccc', borderRadius: '6px', fontSize: '0.88rem', boxSizing: 'border-box' }}
                >
                  <option value="">— None —</option>
                  {DEPT_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              {editAuthorMsg && <div style={{ fontSize: '0.82rem', color: '#dc3545', marginBottom: '0.75rem' }}>{editAuthorMsg}</div>}
              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <button type="submit" style={{ flex: 1, padding: '0.65rem', backgroundColor: '#343a40', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer' }}>
                  Save Changes
                </button>
                <button type="button" onClick={() => setEditAuthorTarget(null)} style={{ flex: 1, padding: '0.65rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.9rem', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove author confirmation modal */}
      {removeTarget && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '400px', width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <h4 style={{ marginBottom: '0.5rem', color: '#1a1a1a' }}>Remove Author</h4>
            <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.25rem' }}>
              You are about to remove <strong>{removeTarget.display_name}</strong> (@{removeTarget.username}).
            </p>
            <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '1.25rem' }}>
              Their login access will be revoked. Articles they created will remain in your silo.
            </p>
            <label style={{ fontSize: '0.82rem', fontWeight: '600', color: '#333', display: 'block', marginBottom: '0.35rem' }}>
              Enter your company password to confirm
            </label>
            <input
              type="password"
              value={removePassword}
              onChange={e => { setRemovePassword(e.target.value); setRemoveError(''); }}
              placeholder="Company password"
              autoFocus
              style={{ width: '100%', padding: '0.55rem 0.75rem', fontSize: '0.9rem', border: `1px solid ${removeError ? '#dc3545' : '#ccc'}`, borderRadius: '6px', marginBottom: '0.4rem', boxSizing: 'border-box' }}
            />
            {removeError && (
              <div style={{ color: '#dc3545', fontSize: '0.8rem', marginBottom: '0.75rem' }}>{removeError}</div>
            )}
            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.75rem' }}>
              <button
                onClick={handleRemoveAuthorConfirm}
                disabled={removeLoading || !removePassword}
                style={{
                  flex: 1, padding: '0.65rem', backgroundColor: removeLoading || !removePassword ? '#aaa' : '#dc3545',
                  color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '600',
                  fontSize: '0.9rem', cursor: removeLoading || !removePassword ? 'not-allowed' : 'pointer',
                }}
              >
                {removeLoading ? 'Removing...' : 'Remove Author'}
              </button>
              <button
                onClick={() => { setRemoveTarget(null); setRemovePassword(''); setRemoveError(''); }}
                style={{ flex: 1, padding: '0.65rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete article confirmation modal ── */}
      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '440px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.25)', fontFamily: 'Arial, sans-serif' }}>
            <div style={{ fontWeight: '700', fontSize: '1.1rem', color: '#cc0000', marginBottom: '0.75rem' }}>🗑️ Delete Article</div>
            <div style={{ fontWeight: '600', fontSize: '0.92rem', color: '#222', marginBottom: '0.5rem' }}>"{deleteTarget.title}"</div>
            {(() => {
              const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
              const lastP = deleteTarget.lastPurchasedAt ? new Date(deleteTarget.lastPurchasedAt) : null;
              const daysSince = lastP ? Math.floor((Date.now() - lastP.getTime()) / 86400000) : null;
              const daysRemaining = lastP ? Math.ceil((THIRTY_DAYS_MS - (Date.now() - lastP.getTime())) / 86400000) : 0;
              const blocked = lastP && (Date.now() - lastP.getTime()) < THIRTY_DAYS_MS;
              return blocked ? (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '0.85rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#664d03' }}>
                  <div style={{ fontWeight: '700', marginBottom: '0.4rem' }}>🔒 Deletion is not available yet</div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    A reader licensed this article <strong>{daysSince} day{daysSince !== 1 ? 's' : ''} ago</strong> — on {lastP.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. When someone licenses your content, PubSilo guarantees them at least 30 days of uninterrupted access. Deleting the article before that window closes would break that promise.
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    Deletion will be available in <strong>{daysRemaining} more day{daysRemaining !== 1 ? 's' : ''}</strong> — on or after {new Date(lastP.getTime() + THIRTY_DAYS_MS).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#856404', borderTop: '1px solid #ffc107', paddingTop: '0.4rem', marginTop: '0.25rem' }}>
                    💡 If you want to stop new readers from licensing this article, use the <strong>Content Access Sunset</strong> feature — set a future date and the article will be removed from public feeds and closed to new readers on that date. Anyone who already has access keeps it.
                  </div>
                </div>
              ) : deleteTarget.purchased > 0 ? (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px', padding: '0.85rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#166534' }}>
                  <strong>✓ 30-day access window satisfied.</strong> Last access was {daysSince} day{daysSince !== 1 ? 's' : ''} ago. This article will be removed. No refunds or debt are generated — all readers had their full access period.
                </div>
              ) : (
                <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '0.85rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#444' }}>
                  This article has no reader accesses. It will be permanently removed.
                </div>
              );
            })()}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.84rem', marginBottom: '0.35rem', color: '#333' }}>Enter your password to confirm:</label>
              <input
                type="password"
                value={deletePassword}
                onChange={e => setDeletePassword(e.target.value)}
                placeholder="Your account password"
                style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #ccc', borderRadius: '7px', fontSize: '0.9rem', boxSizing: 'border-box' }}
              />
            </div>
            {deleteError && (
              <div style={{ color: '#cc0000', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{deleteError}</div>
            )}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {(() => {
                const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
                const lastP = deleteTarget.lastPurchasedAt ? new Date(deleteTarget.lastPurchasedAt) : null;
                const blocked = lastP && (Date.now() - lastP.getTime()) < THIRTY_DAYS_MS;
                return (
                  <button
                    onClick={handleDeleteArticle}
                    disabled={deleteLoading || !deletePassword || blocked}
                    style={{ flex: 1, padding: '0.65rem', backgroundColor: '#cc0000', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.9rem', cursor: deleteLoading || !deletePassword || blocked ? 'not-allowed' : 'pointer', opacity: deleteLoading || !deletePassword || blocked ? 0.6 : 1 }}
                  >
                    {deleteLoading ? 'Deleting…' : 'Confirm Delete'}
                  </button>
                );
              })()}
              <button
                onClick={() => { setDeleteTarget(null); setDeletePassword(''); setDeleteError(''); }}
                style={{ flex: 1, padding: '0.65rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed settings panel — bottom-left */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, width: '220px', zIndex: 1001, background: '#fff', borderRight: `1px solid ${T.border}`, paddingBottom: '10px' }}>
        <SettingsPanel onLogout={handleLogout} />
      </div>

      {/* ── Help & Guide Modal ── */}
      {showHelpModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 3000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }} onClick={e => { if (e.target === e.currentTarget) setShowHelpModal(false); }}>
          <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '680px', boxShadow: '0 8px 40px rgba(0,0,0,0.2)', marginTop: '20px', marginBottom: '20px' }}>
            <div style={{ background: '#13273F', padding: '20px 24px', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '18px', fontWeight: '800', color: '#fff', fontFamily: 'Arial, sans-serif' }}>Publisher Guide</div>
                <div style={{ fontSize: '12px', color: '#8899bb', marginTop: '2px', fontFamily: 'Arial, sans-serif' }}>Everything you need to know to get started</div>
              </div>
              <button onClick={() => setShowHelpModal(false)} style={{ background: 'none', border: 'none', color: '#8899bb', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>
            <div style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(90vh - 80px)', fontFamily: 'Arial, sans-serif' }}>
              {[
                {
                  icon: '📝',
                  title: 'Publishing Articles',
                  body: <>
                    <p style={{ margin: '0 0 0.6rem' }}>Click <strong>+ Create New Article</strong> in the Quick Actions panel. Fill in the title, select a genre, set a credit price, upload a headline image, and write your content in the editor.</p>
                    <p style={{ margin: '0 0 0.6rem' }}><strong>Save as Draft</strong> saves without submitting for review. <strong>Submit for Approval</strong> sends it to an editor (if you have editors) or publishes it directly if you don't.</p>
                    <p style={{ margin: 0 }}>You can also create <strong>External Article Cards</strong> — cards that link readers to content hosted on your own website, without uploading content to PubSilo.</p>
                  </>,
                },
                {
                  icon: '💳',
                  title: 'Article Credits & Pricing',
                  body: <>
                    <p style={{ margin: '0 0 0.6rem' }}>Readers pay using <strong>Article Credits</strong>. Each credit = $0.01. Minimum price is <strong>4 credits ($0.04)</strong>, maximum is <strong>150 credits ($1.50)</strong>.</p>
                    <p style={{ margin: '0 0 0.6rem' }}>Your licensing payment = credits spent × $0.01, minus PubSilo's platform fee (15% on your first $2,000/month of gross revenue — lower rates of 12.5% and 9% apply above that).</p>
                    <p style={{ margin: 0 }}><strong>Tip:</strong> Most articles perform best in the 20–60 credit range ($0.20–$0.60). Readers are comfortable paying under a dollar for content they actually want.</p>
                  </>,
                },
                {
                  icon: '⚙️',
                  title: 'Auto-Pricing',
                  body: <>
                    <p style={{ margin: '0 0 0.6rem' }}>Auto-Pricing gradually reduces an article's price over time. Enable it per-article from the pricing dropdown on any article card.</p>
                    <p style={{ margin: '0 0 0.6rem' }}>Set a <strong>first decrease date</strong> (days after publish), a <strong>decrease %</strong>, and a <strong>repeat frequency</strong>. Optionally set a <strong>skip threshold</strong> — if the article earns above X credits/day, that decrease cycle is skipped.</p>
                    <p style={{ margin: 0 }}>Auto-pricing runs nightly. Prices never drop below the 4-credit minimum.</p>
                  </>,
                },
                {
                  icon: '✍️',
                  title: 'Creating Authors',
                  body: <>
                    <p style={{ margin: '0 0 0.6rem' }}>Authors write and submit articles under your company. Create an author from the <strong>Authors</strong> panel in the right sidebar. They log in with their own credentials.</p>
                    <p style={{ margin: 0 }}>When an author submits an article, it goes to <strong>Pending Review</strong> unless their <strong>Publish without review</strong> checkbox is checked. If checked, it publishes immediately. If unchecked, it waits for approval — from an assigned editor, or from you directly in the article list. Articles you create as the company publisher always go live immediately, no review required.</p>
                  </>,
                },
                {
                  icon: '🖊️',
                  title: 'Creating Editors & the Approval Flow',
                  body: <>
                    <p style={{ margin: '0 0 0.6rem' }}>Editors review and approve or reject articles submitted by authors. Create an editor from the <strong>Editors</strong> panel, then use the gear menu to assign authors to them.</p>
                    <p style={{ margin: '0 0 0.4rem' }}><strong>Editorial flow:</strong></p>
                    <ol style={{ paddingLeft: '1.25rem', margin: '0 0 0.6rem', lineHeight: 2 }}>
                      <li>Author writes and submits an article</li>
                      <li>Assigned editor sees it flagged for review in their dashboard</li>
                      <li>Editor approves → article goes live on your silo</li>
                      <li>Editor rejects → author sees the rejection note and can revise</li>
                    </ol>
                    <p style={{ margin: 0 }}>Without editors, pending author submissions land in your article list for you to approve directly. <strong>Publish without review</strong> always bypasses this — checked authors self-publish regardless of whether editors are set up. Articles you create as the company publisher always go live immediately.</p>
                  </>,
                },
                {
                  icon: '🖼️',
                  title: 'Recommended Image Sizes',
                  body: <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem', marginTop: '0.25rem' }}>
                      <thead>
                        <tr style={{ background: '#f4f4f4' }}>
                          <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: '700' }}>Image</th>
                          <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: '700' }}>Recommended</th>
                          <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', borderBottom: '1px solid #ddd', fontWeight: '700' }}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid #eee' }}>Company Logo / Banner</td>
                          <td style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid #eee' }}>800 × 200 px</td>
                          <td style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid #eee' }}>Shown in your silo header and article feeds. Dark backgrounds recommended. Max 3 MB.</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid #eee' }}>Article Headline Image</td>
                          <td style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid #eee' }}><strong>1200 × 675 px</strong></td>
                          <td style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid #eee' }}><strong>16:9 landscape is required for clean display.</strong> All article feed cards crop to 16:9. Portrait or square images will be cropped — subjects near the top of the image are preserved; centered or bottom-heavy images may be cut off. When in doubt, use a wide landscape photo. Max 3 MB.</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '0.4rem 0.6rem' }}>In-Article Images</td>
                          <td style={{ padding: '0.4rem 0.6rem' }}>Up to 1000 px wide</td>
                          <td style={{ padding: '0.4rem 0.6rem' }}>Any ratio accepted. Alt text is required on all images before publishing.</td>
                        </tr>
                      </tbody>
                    </table>
                  </>,
                },
                {
                  icon: '🌅',
                  title: 'Content Sunset Dates',
                  body: <>
                    <p style={{ margin: '0 0 0.6rem' }}>A sunset date closes an article to new readers on a specific future date. After that date, the article disappears from all public feeds and no new readers can license it.</p>
                    <p style={{ margin: 0 }}>Readers who already purchased the article retain full access past the sunset date. Set or change a sunset date from the pricing dropdown on any article card.</p>
                  </>,
                },
                {
                  icon: '🔗',
                  title: 'Embed Integration',
                  body: <>
                    <p style={{ margin: '0 0 0.6rem' }}>The <strong>Embed Integration</strong> panel (under Settings ⚙️) lets you add PubSilo purchase buttons to your own website.</p>
                    <p style={{ margin: '0 0 0.6rem' }}><strong>Elevated tier:</strong> A "Buy on PubSilo" link button that sends readers to PubSilo to complete the purchase for a specific article.</p>
                    <p style={{ margin: 0 }}><strong>Grand tier:</strong> A fully embedded purchase flow hosted directly on your site. Requires API keys — generate them from the Embed panel. Keys are shown only once on generation, so copy them immediately.</p>
                  </>,
                },
                {
                  icon: '📋',
                  title: 'W-9 Tax Information',
                  body: <>
                    <p style={{ margin: '0 0 0.6rem' }}>PubSilo is required to collect a W-9 from any publisher who earns $600 or more in a calendar year. Submit your W-9 early from <strong>Settings ⚙️ → Tax Info / W-9</strong> so payments aren't interrupted when you hit that threshold.</p>
                    <p style={{ margin: 0 }}>Your TIN (SSN or EIN) is encrypted at rest using AES-256-GCM. Only the last 4 digits are stored for verification — no PubSilo staff can view the full number.</p>
                  </>,
                },
              ].map(({ icon, title, body }) => (
                <div key={title} style={{ marginBottom: '1.75rem', paddingBottom: '1.75rem', borderBottom: '1px solid #eee' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.65rem' }}>
                    <span style={{ fontSize: '1.1rem' }}>{icon}</span>
                    <div style={{ fontSize: '1rem', fontWeight: '800', color: '#13273F' }}>{title}</div>
                  </div>
                  <div style={{ fontSize: '0.87rem', color: '#444', lineHeight: 1.7 }}>{body}</div>
                </div>
              ))}
              <div style={{ textAlign: 'center', paddingTop: '0.25rem' }}>
                <p style={{ fontSize: '0.82rem', color: '#888', margin: 0 }}>Still have questions? Use <strong>Contact Support</strong> from the Settings ⚙️ menu.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Company Support Contact Modal ── */}
      {supportOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '500px', boxShadow: '0 8px 40px rgba(0,0,0,0.18)', overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ background: '#1a1a2e', padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '800', color: '#fff' }}>Contact Support</div>
                <div style={{ fontSize: '12px', color: '#8899aa', marginTop: '2px' }}>{company?.name}</div>
              </div>
              <button onClick={() => setSupportOpen(false)} style={{ background: 'none', border: 'none', color: '#8899aa', fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>

            {supportResult === 'ok' ? (
              <div style={{ padding: '40px 28px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', marginBottom: '14px' }}>✅</div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '8px' }}>Message sent</div>
                <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.6', marginBottom: '24px' }}>
                  We received your request and will follow up at <strong>{company?.email}</strong>.
                </div>
                <button onClick={() => setSupportOpen(false)} style={{ padding: '9px 24px', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', cursor: 'pointer', fontSize: '14px' }}>
                  Close
                </button>
              </div>
            ) : (
              <div style={{ padding: '22px 24px 26px' }}>

                {/* Topic */}
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  Topic
                </label>
                <select
                  value={supportTopic}
                  onChange={e => { setSupportTopic(e.target.value); setSupportErr(''); }}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: '7px', fontSize: '14px', color: supportTopic ? '#222' : '#999', marginBottom: '16px', background: '#fff' }}
                >
                  <option value="">— Select a topic —</option>
                  <option value="billing">💳 Billing &amp; Payouts</option>
                  <option value="deletion_debt">⚠️ Deletion Debt / Payout Hold</option>
                  <option value="account">🔐 Account Support</option>
                  <option value="technical">🛠️ Technical Issue</option>
                  <option value="pricing">📊 Auto-Pricing / Article Settings</option>
                  <option value="content">🚩 Content Violation / Copyright</option>
                  <option value="dmca">📋 DMCA Takedown Request</option>
                  <option value="flagged">🚫 Restricted / Flagged Account</option>
                  <option value="press">📰 Press / Media Inquiry</option>
                  <option value="legal">⚖️ Legal / Compliance</option>
                  <option value="advertising">📣 Advertising Partnership</option>
                  <option value="other">❓ Other</option>
                </select>

                {/* Routing hint */}
                {supportTopic && (() => {
                  const hints = {
                    billing:       { label: 'billing@pubsilo.com', note: 'Invoice and payout questions' },
                    deletion_debt: { label: 'admin@pubsilo.com',   note: 'Payout holds and debt resolution' },
                    account:       { label: 'support@pubsilo.com', note: 'Login, account, and profile help' },
                    technical:     { label: 'support@pubsilo.com', note: 'Platform bugs and technical issues' },
                    pricing:       { label: 'support@pubsilo.com', note: 'Auto-pricing configuration and articles' },
                    content:       { label: 'content@pubsilo.com', note: 'Copyright, editorial, and content policy' },
                    dmca:          { label: 'content@pubsilo.com', note: 'Formal DMCA takedown requests' },
                    flagged:       { label: 'admin@pubsilo.com',   note: 'Account restriction and review' },
                    press:         { label: 'press@pubsilo.com',   note: 'Media and press inquiries' },
                    legal:         { label: 'admin@pubsilo.com',   note: 'Legal and compliance matters' },
                    advertising:   { label: 'hello@pubsilo.com',   note: 'Partnership and advertising opportunities' },
                    other:         { label: 'support@pubsilo.com', note: 'General questions' },
                  };
                  const h = hints[supportTopic];
                  return h ? (
                    <div style={{ background: '#f0f4ff', borderRadius: '7px', padding: '8px 12px', fontSize: '12px', color: '#4a6a9a', marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span style={{ fontWeight: '700' }}>→</span>
                      <span>Your message will be routed to <strong>{h.label}</strong> — {h.note}.</span>
                    </div>
                  ) : null;
                })()}

                {/* Message */}
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  Details
                </label>
                <textarea
                  value={supportMsg}
                  onChange={e => { setSupportMsg(e.target.value); setSupportErr(''); }}
                  placeholder="Describe your issue or question in as much detail as possible…"
                  rows={5}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '7px', fontSize: '13px', lineHeight: '1.6', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}
                />

                {supportErr && (
                  <div style={{ fontSize: '13px', color: '#cc0000', marginTop: '8px' }}>{supportErr}</div>
                )}

                <div style={{ fontSize: '11px', color: '#aaa', marginTop: '8px', marginBottom: '18px' }}>
                  We will reply to <strong>{company?.email}</strong>. Response time is typically 1 business day.
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    disabled={supportSending}
                    onClick={async () => {
                      if (!supportTopic) return setSupportErr('Please select a topic.');
                      if (supportMsg.trim().length < 10) return setSupportErr('Please provide more detail.');
                      setSupportSending(true);
                      try {
                        await axios.post('/api/contact/company-support', { topic: supportTopic, message: supportMsg });
                        setSupportResult('ok');
                      } catch (e) {
                        setSupportErr(e.response?.data?.error || 'Failed to send. Please try again.');
                      } finally {
                        setSupportSending(false);
                      }
                    }}
                    style={{ flex: 1, padding: '10px', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '14px', cursor: supportSending ? 'not-allowed' : 'pointer', opacity: supportSending ? 0.7 : 1 }}
                  >
                    {supportSending ? 'Sending…' : 'Send Message'}
                  </button>
                  <button
                    onClick={() => setSupportOpen(false)}
                    style={{ padding: '10px 18px', background: '#f4f4f4', color: '#555', border: '1px solid #ddd', borderRadius: '7px', fontSize: '14px', cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// Instructions modal styles
const instrStep  = { display: 'flex', gap: '0.85rem', marginBottom: '1.1rem', alignItems: 'flex-start' };
const instrNum   = { flexShrink: 0, width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#0d3165', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '0.78rem', marginTop: '0.1rem' };
const instrTitle = { fontWeight: '700', fontSize: '0.88rem', color: '#0d3165', marginBottom: '0.3rem' };
const instrBody  = { fontSize: '0.83rem', color: '#444', lineHeight: 1.6, margin: 0 };

// Shared style for embed generator inputs
const inputStyle = {
  width: '100%', padding: '0.4rem 0.6rem', border: '1px solid #ccc',
  borderRadius: '5px', fontSize: '0.8rem', marginBottom: '0.4rem',
  boxSizing: 'border-box',
};

const thStyle = { padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: '600', color: '#555', borderBottom: '1px solid #ddd', whiteSpace: 'nowrap' };
const tdStyle = { padding: '0.35rem 0.5rem', color: '#333', verticalAlign: 'middle' };

export default CompanyDashboard;
